import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { MAX_TILES } from '../../constants';
import { formatDirection } from '../../utils/shipmentHelpers';
import { isDockUnavailableForMovement } from '../../utils/dock';
import { useModalDestinations } from 'hooks/useModalDestinations';
import { useCampusDestinations } from 'hooks/useCampusDestinations';
import { facilityDisplayName, facilityLabelById } from 'utils/campusLabels';
import { withCampusFacilities, yardCheckContext } from 'utils/movementVariables';
import { detectAndroidDevice } from '../../utils/deviceDetection';
import { 
  CircularProgress, 
  Modal, 
  Tooltip, 
  Paper, 
  Tab, 
  Tabs, 
  Box, 
  ToggleButton, 
  ToggleButtonGroup, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  IconButton, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText, 
  Avatar, 
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  TextField,
  Autocomplete
} from '@mui/material';
import { 
  Close, 
  Edit, 
  Check, 
  ArrowForward, 
  ArrowBack,
  ArrowForwardIos,
  History, 
  SwapHoriz, 
  FilterList, 
  Add, 
  Delete,
  LocalParking,
  LocalShipping,
  Refresh
} from '@mui/icons-material';
import Select from 'react-select';
import { twMerge } from 'tailwind-merge';
import { useYardData, YardSpot, Status, Zone, HistoryEntry, YardHistory } from './yardDataFetch';
import TaskDetailsModal from '../../components/TaskDetailsModal/TaskDetailsModal';
import { getAuthToken } from '../../utils/cookieUtils';
import {
  ZoneManagementModal,
  ZoneEditModal,
  MoveToDockModal,
  TaskSuccessModal,
  RemoveShipmentConfirmDialog,
  ZoneTrailerDetailsModal,
  MoveToYardSpotModal,
  UnifiedDetailsModal,
  SpotterTruckDialog
} from '../../components/YardManagement';
import {
  StagingTrailer,
  ZoneTrailer,
  LocalYardSpot,
  UnifiedModalItem,
  ExistingTrailer,
  TileProps,
  DockDoorInfo,
  FacilityZone
} from './types';
import {
  validateShipmentReference,
  createMovementTask,
  swapTrailersBetweenSpots,
  assignTrailerToYardSpot,
  unassignTrailerFromYardSpot,
  updateTrailerNotes,
  updateTrailerShipment,
  removeShipmentRelationship,
  assignTrailerToDock,
  getShipmentByReferenceNumber,
  addTrailer,
  updateTrailer,
  getTaskByUuid,
  createAudit,
  getYardCheckSpotterContext,
  SpotterTruck,
  YardCheckSpotterContext,
  GRAPHQL_API_URL
} from './api';
import { lineageCheckIn } from 'utils/lineage';
import { loadSpotterTruck, saveSpotterTruck, StoredSpotterTruck } from '../../utils/spotterTruckStorage';

// A zone's spots in decade ranges (1-10, 11-20, 191-200, ...), labelled by the
// first and last spot number actually in each range.
type SpotBatch = { label: string; start: number; end: number };

// What batching needs of a spot: the page's YardSpot, or a campus destination spot.
type CheckSpot = { id: string; zoneId: string; displayNumber: string };

const spotNumber = (displayNumber: string): number | null => {
  const match = displayNumber.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

const spotBatchesForZone = (spots: CheckSpot[], zoneId: string | null): SpotBatch[] => {
  if (!spots || spots.length === 0 || !zoneId) return [];

  // Filter spots by the zone - don't apply SCAC filter here
  const uniqueNumbers = Array.from(new Set(
    spots
      .filter(spot => spot.zoneId === zoneId)
      .map(spot => spotNumber(spot.displayNumber))
      .filter((num): num is number => num !== null)
  )).sort((a, b) => a - b);

  if (uniqueNumbers.length === 0) return [];

  const batches: SpotBatch[] = [];
  const maxNumber = uniqueNumbers[uniqueNumbers.length - 1];

  // Calculate the starting decade range
  let rangeStart = Math.floor((uniqueNumbers[0] - 1) / 10) * 10 + 1;

  while (rangeStart <= maxNumber) {
    const rangeEnd = rangeStart + 9;
    const spotsInRange = uniqueNumbers.filter(num => num >= rangeStart && num <= rangeEnd);

    if (spotsInRange.length > 0) {
      // Use the actual first and last spot numbers in this range
      const actualStart = spotsInRange[0];
      const actualEnd = spotsInRange[spotsInRange.length - 1];
      batches.push({ label: `${actualStart}-${actualEnd}`, start: actualStart, end: actualEnd });
    }

    rangeStart += 10;
  }

  return batches;
};

// A zone's spots, within one batch when given, deduplicated by id.
const spotsInBatch = <S extends CheckSpot>(spots: S[], zoneId: string, batch: SpotBatch | null): S[] => {
  const inBatch = spots.filter(spot => {
    if (spot.zoneId !== zoneId) return false;
    if (!batch) return true;
    const num = spotNumber(spot.displayNumber);
    return num !== null && num >= batch.start && num <= batch.end;
  });
  return inBatch.filter((spot, index, self) => index === self.findIndex(s => s.id === spot.id));
};

// One drive through the campus: the zone checks started by "Next zone" or the
// zone picker share it. `facilityId` is the facility of the zone being checked
// (absent on runs saved before campus-wide checks).
type YardCheckRun = { runUuid: string; truck: StoredSpotterTruck | null; facilityId?: string };

// A zone a yard check can move to, anywhere on the campus.
/** `facilityName` and `facilityAlias` become the selected facility on a switch; `facilityLabel` is what's shown. */
type CheckZone = {
  id: string;
  name: string;
  facilityId: string;
  facilityName: string;
  facilityAlias?: string | null;
  facilityLabel: string;
};

// The zone picker's options, grouped by facility.
type CheckZoneOption = { value: string; label: string; zone: CheckZone };
type CheckZoneGroup = { label: string; options: CheckZoneOption[] };

// Zones the page never offers for a yard check (see yardDataFetch).
const isUncheckedZone = (name: string) => name === 'Staging Area' || name === 'Main Yard';

const YARD_CHECK_RUN_STORAGE_KEY = 'yard_check_run';

const newRunUuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // crypto.randomUUID needs a secure context; fall back to a random v4 uuid.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const loadYardCheckRun = (): YardCheckRun | null => {
  try {
    const stored = localStorage.getItem(YARD_CHECK_RUN_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as YardCheckRun) : null;
  } catch {
    return null;
  }
};

const saveYardCheckRun = (run: YardCheckRun | null): void => {
  try {
    if (run) {
      localStorage.setItem(YARD_CHECK_RUN_STORAGE_KEY, JSON.stringify(run));
    } else {
      localStorage.removeItem(YARD_CHECK_RUN_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to save yard check run:', error);
  }
};

// Helper to merge lock sets
const mergeLockSets = (lockSet1: Set<string>, lockSet2: Set<string>): Set<string> => {
  const merged = new Set<string>();
  lockSet1.forEach(id => merged.add(id));
  lockSet2.forEach(id => merged.add(id));
  return merged;
};

// Helper functions
const getRandomColor = () => {
  const colors = [
    '#4CAF50', '#2196F3', '#FF9800', '#E91E63', 
    '#9C27B0', '#00BCD4', '#FFEB3B', '#795548'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Reusable Tile Container Component (with optional arrows)
interface TileContainerProps {
  children: React.ReactNode;
  showLeftArrow?: boolean;
  showRightArrow?: boolean;
  onLeftClick?: () => void;
  onRightClick?: () => void;
  leftArrowDisabled?: boolean;
  rightArrowDisabled?: boolean;
  leftArrowColor?: string;
  rightArrowColor?: string;
  leftArrowIcon?: React.ReactNode;
  rightArrowIcon?: React.ReactNode;
}

const TileContainer: React.FC<TileContainerProps> = ({
  children,
  showLeftArrow = false,
  showRightArrow = false,
  onLeftClick,
  onRightClick,
  leftArrowDisabled = false,
  rightArrowDisabled = false,
  leftArrowColor = '#3b82f6',
  rightArrowColor = '#3b82f6',
  leftArrowIcon,
  rightArrowIcon
}) => {
  return (
    <div className="flex items-center justify-center gap-1">
      {/* Left arrow or spacer */}
      {showLeftArrow ? (
        <button
          onClick={(e) => {
            if (!leftArrowDisabled && onLeftClick) {
              e.stopPropagation();
              onLeftClick();
            }
          }}
          disabled={leftArrowDisabled}
          style={{ 
            width: '32px', 
            height: '32px',
            backgroundColor: leftArrowDisabled ? '#d1d5db' : leftArrowColor,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: leftArrowDisabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            padding: leftArrowIcon ? '4px' : '0'
          }}
        >
          {leftArrowIcon || '◀'}
        </button>
      ) : (
        <div style={{ width: 32 }} />
      )}
      
      {/* Tile */}
      {children}
      
      {/* Right arrow or spacer */}
      {showRightArrow ? (
        <button
          onClick={(e) => {
            if (!rightArrowDisabled && onRightClick) {
              e.stopPropagation();
              onRightClick();
            }
          }}
          disabled={rightArrowDisabled}
          style={{ 
            width: '32px', 
            height: '32px',
            backgroundColor: rightArrowDisabled ? '#d1d5db' : rightArrowColor,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: rightArrowDisabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            padding: rightArrowIcon ? '4px' : '0'
          }}
        >
          {rightArrowIcon || '▶'}
        </button>
      ) : (
        <div style={{ width: 32 }} />
      )}
    </div>
  );
};

// Reusable Tile Component

const Tile: React.FC<TileProps> = ({ 
  label, 
  status, 
  zoneColor, 
  onClick, 
  tooltip, 
  actionButton, 
  actionButtons,
  spotName,
  trailerNumber,
  carrierName,
  shipmentNumber,
  isZoneTrailer = false,
  isLocked = false,
  onTaskClick,
  isTaskBasedLock = false,
  shipmentDirection = null,
  locks = [],
  notes,
  isHighlighted = false,
  isVerified = false,
  onVerify
}) => {
  // Detect if on Android device for responsive sizing
  const isAndroid = detectAndroidDevice();
  
  // Helper function to get lock display text
  const getLockDisplayText = () => {
    if (!isTaskBasedLock) {
      return 'Processing...';
    }
    
    // Always show "Active Task" for any task-based lock, regardless of count
    return 'Active Task';
  };

  const getStatusColors = () => {
    switch (status) {
      case 'available':
        return { bg: 'bg-green-100', border: 'border-green-400', header: 'bg-green-500' };
      case 'in-use':
        return { bg: 'bg-white', border: 'border-gray-400', header: 'bg-gray-500' };
      case 'inactive':
        return { bg: 'bg-red-100', border: 'border-red-400', header: 'bg-red-500' };
      case 'empty':
        return { bg: 'bg-yellow-200', border: 'border-yellow-400', header: 'bg-yellow-500' };
      case 'loaded':
        return { bg: 'bg-blue-100', border: 'border-blue-400', header: 'bg-blue-500' };
      case 'inbound':
        return { bg: 'bg-yellow-200', border: 'border-green-600', header: 'bg-green-600' };
      case 'outbound':
        return { bg: 'bg-blue-100', border: 'border-blue-400', header: 'bg-blue-500' };
      case 'unavailable':
        return { bg: 'bg-gray-100', border: 'border-gray-400', header: 'bg-gray-500' };
      default:
        return { bg: 'bg-yellow-100', border: 'border-yellow-400', header: 'bg-yellow-500' };
    }
  };

  const colors = getStatusColors();


  
  // Determine which buttons to show
  const buttonsToShow = actionButtons || (actionButton ? [actionButton] : []);
  
  const tileContent = (
    <div
      className={`w-32 h-32 rounded shadow cursor-pointer flex flex-col relative ${colors.bg} ${colors.border} border-2 hover:opacity-90 transition-opacity flex-shrink-0 overflow-hidden ${isHighlighted ? 'ring-4 ring-blue-500 ring-offset-2 scale-105 z-10' : ''}`}
      onClick={onClick}
      style={{ borderColor: zoneColor, minWidth: '128px', maxWidth: '128px' }}
    >
      {/* Gray overlay for locked tiles */}
      {isLocked && (
        <div 
          className="absolute inset-0 bg-gray-500 bg-opacity-50 rounded flex items-center justify-center z-20 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (isTaskBasedLock && onTaskClick) {
              onTaskClick();
            }
          }}
        >
          <div className="bg-white px-2 py-1 rounded text-xs font-medium text-gray-700 shadow">
            {getLockDisplayText()}
          </div>
        </div>
      )}
      <div className={`h-4 w-full rounded-t ${colors.header}`}></div>
      
      {/* Spot name in top left corner (for yard spots only) */}
      {!isZoneTrailer && spotName && (
        <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
          {spotName}
        </div>
      )}
      
      {/* Shipment direction indicator in top right corner */}
      {shipmentDirection && (
        <div className="absolute top-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
          {shipmentDirection === 'inbound' ? 'I' : 'O'}
        </div>
      )}
      
      {/* Main content area */}
      <div className="flex flex-1 flex-col px-2 py-1">
        {trailerNumber ? (
          // Show trailer info when there's a trailer - pinned to top
          <div className="text-center mt-1">
            <div className="font-bold text-sm mb-1 leading-tight">{trailerNumber}</div>
            {carrierName && (
              <div className="text-xs text-gray-700 font-medium leading-tight">{carrierName}</div>
            )}
            {shipmentNumber && (
              <div className="text-xs text-gray-500 leading-tight">{shipmentNumber}</div>
            )}
          </div>
        ) : isZoneTrailer ? (
          // For zone trailers, show the label (trailer number) - pinned to top
          <div className="text-center mt-1">
            <span className="font-bold text-lg leading-tight">{label}</span>
          </div>
        ) : (
          // For empty yard spots, show "Available" or status - centered
          <div className="flex flex-1 items-center justify-center">
            {status === 'unavailable' ? (
              // Show notes for unavailable spots
              <div className="text-center px-1">
                <div className="font-medium text-sm text-red-700 mb-1">Unavailable</div>
                <div className="text-xs text-gray-600 leading-tight break-words">
{notes ? notes : `[No notes - notes value: "${notes}"]`}
                </div>
              </div>
            ) : (
              <span className="font-medium text-sm text-center leading-tight text-gray-600">
                {status === 'available' ? 'Available' : status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      {(buttonsToShow.length > 0 || onVerify) && (
        <div className={`absolute bottom-0.5 left-0.5 right-0.5 flex flex-col`} style={{ gap: isAndroid ? '1px' : '2px' }}>
          {/* Action buttons in a row if verify button present */}
          {onVerify && buttonsToShow.length > 0 ? (
            <div className="flex gap-0.5">
              {/* Red X button (narrower) */}
              {buttonsToShow.map((button, index) => (
                <button
                  key={index}
                  disabled={button.disabled}
                  className={`tile-action-button flex-1 rounded shadow-sm ${
                    button.disabled ? 'bg-gray-400 text-white cursor-not-allowed' : (button.className || 'bg-blue-500 hover:bg-blue-600 text-white')
                  }`}
                  style={{ 
                    fontSize: isAndroid ? '14px' : '1.5rem', 
                    lineHeight: '1', 
                    padding: isAndroid ? '8px 4px' : '8px 4px',
                    height: isAndroid ? '36px' : 'auto',
                    minHeight: '36px',
                    maxHeight: isAndroid ? '36px' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: button.disabled ? 0.6 : 1
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!button.disabled) {
                      button.onClick(e);
                    }
                  }}
                  title={button.label}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isAndroid ? '1px' : '4px', lineHeight: '1' }}>
                    {button.icon && <span style={{ fontSize: isAndroid ? '7px' : '12px', lineHeight: '1', display: 'flex', alignItems: 'center' }}>{button.icon}</span>}
                    <span style={{ lineHeight: '1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{button.label}</span>
                  </div>
                </button>
              ))}
              {/* Green check button */}
              <button
                className={`tile-action-button flex-1 rounded shadow-sm ${
                  isVerified
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-300 text-gray-600 border-2 border-gray-400'
                } hover:bg-green-700 hover:text-white hover:border-green-700`}
                style={{ 
                  fontSize: isAndroid ? '14px' : '1.5rem', 
                  lineHeight: '1', 
                  padding: isAndroid ? '8px 4px' : '8px 4px',
                  height: isAndroid ? '36px' : 'auto',
                  minHeight: '36px',
                  maxHeight: isAndroid ? '36px' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onVerify) {
                    onVerify(e);
                  }
                }}
                title={isVerified ? "Verified - Click to unverify" : "Click to verify spot"}
              >
                ✓
              </button>
            </div>
          ) : onVerify ? (
            // Only verify button
            <button
              className={`tile-action-button w-full rounded shadow-sm ${
                isVerified
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-300 text-gray-600 border-2 border-gray-400'
              } hover:bg-green-700 hover:text-white hover:border-green-700`}
              style={{ 
                fontSize: isAndroid ? '14px' : '1.5rem', 
                lineHeight: '1', 
                padding: isAndroid ? '8px 4px' : '8px 4px',
                height: isAndroid ? '36px' : 'auto',
                minHeight: '36px',
                maxHeight: isAndroid ? '36px' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onVerify) {
                  onVerify(e);
                }
              }}
              title={isVerified ? "Verified - Click to unverify" : "Click to verify spot"}
            >
              ✓
            </button>
          ) : (
            // Only action buttons (no verify)
            buttonsToShow.map((button, index) => (
              <button
                key={index}
                disabled={button.disabled}
                className={`tile-action-button w-full rounded shadow-sm ${
                  button.disabled ? 'bg-gray-400 text-white cursor-not-allowed' : (button.className || 'bg-blue-500 hover:bg-blue-600 text-white')
                }`}
                style={{ 
                  fontSize: isAndroid ? '14px' : '1.5rem', 
                  lineHeight: '1', 
                  padding: isAndroid ? '8px 4px' : '8px 4px',
                  height: isAndroid ? '36px' : 'auto',
                  minHeight: '36px',
                  maxHeight: isAndroid ? '36px' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: button.disabled ? 0.6 : 1
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!button.disabled) {
                    button.onClick(e);
                  }
                }}
                title={button.label}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isAndroid ? '1px' : '4px', lineHeight: '1' }}>
                  {button.icon && <span style={{ fontSize: isAndroid ? '7px' : '12px', lineHeight: '1', display: 'flex', alignItems: 'center' }}>{button.icon}</span>}
                  <span style={{ lineHeight: '1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{button.label}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {tileContent}
    </Tooltip>
  ) : tileContent;
};

// Add this function right after your existing helper functions (around line 100)
// Function to determine if a yard spot contains an empty trailer
const isEmptyTrailer = (spot: LocalYardSpot) => {
  return spot.status === 'empty';
};

// Function to determine if a yard spot contains a loaded trailer
const isLoadedTrailer = (spot: YardSpot | LocalYardSpot) => {
  return spot.status === 'loaded';
};

// Function to determine if a trailer is out of service (OOS)
const isTrailerOOS = (trailer: LocalYardSpot | ZoneTrailer) => {
  return trailer.data?.oos === true;
};

// Utility functions for unified modal
const createUnifiedItemFromSpot = (spot: LocalYardSpot, zones: Zone[]): UnifiedModalItem => {
  return {
    type: 'yard_spot',
    id: spot.id,
    displayName: `Spot ${spot.displayNumber}`,
    
    // Trailer info
    trailerNumber: spot.trailerNumber,
    carrierName: spot.carrier_name,
    shipmentNumber: spot.shipmentNumber,
    direction: spot.direction,
    status: spot.trailerStatus,
    
    // Driver info
    driverId: spot.driverId,
    driverName: spot.driverName,
    
    // Yard spot specific
    spotNumber: spot.displayNumber,
    spotStatus: spot.status,
    zoneId: spot.zoneId,
    notes: spot.notes,
    active_lineage: spot.active_lineage,
    zoneName: zones.find(z => z.id === spot.zoneId)?.name,
    lastRtlsEmptyAt: spot.lastRtlsEmptyAt,
    lastManualCheckAt: spot.lastManualCheckAt,
    
    // Original data
    originalData: spot
  };
};

const createUnifiedItemFromZoneTrailer = (trailer: ZoneTrailer, zones: Zone[]): UnifiedModalItem => {
  return {
    type: 'zone_trailer',
    id: trailer.puid,
    displayName: `Trailer ${trailer.trailer_number}`,
    
    // Trailer info
    trailerNumber: trailer.trailer_number,
    carrierName: trailer.carrier_name,
    shipmentNumber: trailer.shipment_number,
    direction: trailer.direction,
    status: trailer.current_status,
    
    // Driver info
    driverId: trailer.driver_id,
    driverName: trailer.driver_name,
    driverPhone: trailer.driver_phone_number,
    
    // Zone trailer specific
    puid: trailer.puid,
    currentZone: zones.find(z => z.id === trailer.zone_id)?.name || 'Unknown Zone',
    trailerUuid: trailer.trailer_uuid,
    active_lineage: trailer.active_lineage,
    
    // Original data
    originalData: trailer
  };
};

// Component
const YardSpotterManagement = React.memo(function YardSpotManagement() {
  // *** YARD SPOT ORDERING STRATEGY ***
  // This component uses a two-step discrete approach to ensure yard spot positioning is never affected by trailer assignments:
  // Step 1: Establish yard spot positions based on their original API order (order_number field, then displayNumber for secondary sort)
  // Step 2: Populate those spots with trailer information without affecting the established order
  // This ensures that spots maintain their physical positions regardless of what trailers are assigned to them.
  // Note: Since order_number often defaults to 0, we use displayNumber (e.g., "A1", "A2") as secondary sort for consistent ordering.
  
  // *** PERFORMANCE OPTIMIZATION ***
  // This component implements several optimizations to prevent unnecessary re-renders:
  // 1. Data fetching is debounced to prevent excessive API calls
  // 2. State updates only occur when data actually changes (using JSON string comparison)
  // 3. Manual refresh allows users to explicitly update data when needed
  // 4. The useYardData hook implements memoization and selective updates
  // 5. Callback functions are memoized to prevent recreation on each render
  // 6. Yard spot assignments and removals update state directly without reloading all data
  // These optimizations significantly improve performance and user experience.
  
  // Local UI state declaration first
  // LocalStorage keys for yard management preferences
  const STORAGE_KEYS = {
    ZONE_FILTER: 'yardManagement_zoneFilter',
    SPOT_BATCH: 'yardManagement_spotBatch',
    SHOW_UNALLOCATED: 'yardManagement_showUnallocated'
  };
  
  // Load saved preferences from localStorage
  const loadSavedPreferences = () => {
    try {
      const savedZone = localStorage.getItem(STORAGE_KEYS.ZONE_FILTER);
      const savedBatch = localStorage.getItem(STORAGE_KEYS.SPOT_BATCH);
      const savedShowUnallocated = localStorage.getItem(STORAGE_KEYS.SHOW_UNALLOCATED);
      
      return {
        zoneFilter: savedZone || null,
        spotBatch: savedBatch || null,
        showUnallocated: savedShowUnallocated === 'true' // Default false
      };
    } catch (error) {
      console.error('Error loading saved preferences:', error);
      return { zoneFilter: null, spotBatch: null, showUnallocated: false };
    }
  };
  
  const savedPrefs = loadSavedPreferences();
  
  const [filterZone, setFilterZone] = useState<string | null>(savedPrefs.zoneFilter);
  const [filterStatus, setFilterStatus] = useState<Status | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tabValue, setTabValue] = useState<number>(0);
  const [spotBatchFilter, setSpotBatchFilter] = useState<string | null>(savedPrefs.spotBatch);
  const [showUnallocatedAssets, setShowUnallocatedAssets] = useState<boolean>(false);
  const [scacFilters, setScacFilters] = useState<string[]>([]);

  
  // Unified modal state
  const [showUnifiedDetailsModal, setShowUnifiedDetailsModal] = useState<boolean>(false);
  const [selectedUnifiedItem, setSelectedUnifiedItem] = useState<UnifiedModalItem | null>(null);
  
  // Quick Add Trailer modal state
  const [showQuickAddModal, setShowQuickAddModal] = useState<boolean>(false);
  const [quickAddSpot, setQuickAddSpot] = useState<LocalYardSpot | null>(null);
  const [quickAddScac, setQuickAddScac] = useState<string>('');
  const [quickAddNumber, setQuickAddNumber] = useState<string>('');
  const [quickAddStatus, setQuickAddStatus] = useState<'EMPTY' | 'LOADED'>('EMPTY');
  const [quickAddSearchInput, setQuickAddSearchInput] = useState<string>('');
  const [quickAddSelectedExisting, setQuickAddSelectedExisting] = useState<ExistingTrailer | null>(null);
  const [quickAddError, setQuickAddError] = useState<string | null>(null);
  const [showQuickAddCreateAnywayButton, setShowQuickAddCreateAnywayButton] = useState<boolean>(false);
  const [isQuickAdding, setIsQuickAdding] = useState<boolean>(false);

  const [showZoneManageModal, setShowZoneManageModal] = useState<boolean>(false);
  const [showZoneEditModal, setShowZoneEditModal] = useState<boolean>(false);
  const [newZoneName, setNewZoneName] = useState<string>('');
  const [newZoneColor, setNewZoneColor] = useState<string>(getRandomColor());
  const [selectedSpot, setSelectedSpot] = useState<LocalYardSpot | null>(null);

  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  
  // Create ref for main content area to maintain its dimensions
  const spotsContainerRef = useRef<HTMLDivElement>(null);
  const [spotsHeight, setSpotsHeight] = useState<number | null>(null);
  
  // Use a more robust scroll restoration approach
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const scrollY = useRef(0);
  
  // Fix the useYardData call with proper property names - use filterZone for API filtering
  const {
    loading: apiLoading,
    yardSpots: rawYardSpots,
    error,
    facilities,
    selectedFacility,
    setSelectedFacility,
    timeInterval,
    setTimeInterval,
    zones,
    facilityZones,
    allZones,
    allFacilityZones,
    allYardSpots,
    warehouseData,
    refreshData,
    refreshZone,
    // Task-based locks
    taskBasedLockedSpots,
    taskBasedLockedTrailers,
    activeLocks,
    allFacilityLocks,
    getLocksForYardSpot
  } = useYardData(undefined, 60000, null); // Use 60 second refresh interval, null for zone filter (always fetch all)
  // The stored selection can predate facility_alias, so label it from the fetched list.
  const selectedFacilityLabel = facilityDisplayName(
    facilities.find(f => f.id === selectedFacility?.id) ?? selectedFacility
  );

  // Add state for local modifications to yardSpots, with extended type to include carrier_name
  const [localModifications, setLocalModifications] = useState<{[spotId: string]: Partial<YardSpot> & {carrier_name?: string | null}}>({});

  // Always use max time interval
  useEffect(() => {
    setTimeInterval('8760'); // Always set to max (1 year)
  }, [setTimeInterval]);
  
  // Step 1: Establish yard spot positions based on their original order
  const sortedYardSpots = useMemo(() => {
    // If the API returns empty array, return empty array
    if (rawYardSpots.length === 0) return [];
    

    
    // Preserve the original API ordering - this should already be sorted by order_number
    // Do NOT apply any modifications here that could affect ordering
    return [...rawYardSpots];
  }, [rawYardSpots]);

  // Step 2: Populate spots with trailer information as a separate step
  const yardSpots = useMemo(() => {
    // Apply local modifications on top of the ordered spots
    // This step only adds/modifies trailer data, never changes positioning
    return sortedYardSpots.map(spot => {
      const modifications = localModifications[spot.id];
      
      // Apply modifications if they exist, otherwise just use the original spot
      // Preserve order_number and other positioning fields from the original spot
      return { 
        ...spot, 
        ...(modifications || {}),
        // Explicitly preserve ordering fields to prevent modification
        order_number: spot.order_number, // Always use original order_number
        displayNumber: spot.displayNumber, // Always use original displayNumber
        number: spot.number // Always use original number
      } as LocalYardSpot;
    });
  }, [sortedYardSpots, localModifications]);

  // Function to save zone filter preference
  const saveZoneFilterPreference = useCallback(() => {
    try {
      if (filterZone) {
        localStorage.setItem(STORAGE_KEYS.ZONE_FILTER, filterZone);
      } else {
        localStorage.removeItem(STORAGE_KEYS.ZONE_FILTER);
      }
    } catch (error) {
      console.error('Error saving zone filter preference:', error);
    }
  }, [filterZone, STORAGE_KEYS.ZONE_FILTER]);

  // Function to clear all filters
  const clearAllFilters = useCallback(() => {
    // Clear state
    setFilterZone(null);
    setSpotBatchFilter(null);
    setShowUnallocatedAssets(false);
    
    // Clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEYS.ZONE_FILTER);
      localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
      localStorage.removeItem(STORAGE_KEYS.SHOW_UNALLOCATED);
    } catch (error) {
      console.error('Error clearing filter preferences:', error);
    }
  }, [STORAGE_KEYS.ZONE_FILTER, STORAGE_KEYS.SPOT_BATCH, STORAGE_KEYS.SHOW_UNALLOCATED]);

  // Spot ranges of the selected zone
  const generateSpotBatches = useMemo(
    () => spotBatchesForZone(yardSpots, filterZone),
    [yardSpots, filterZone]
  );

  
  // Use derivation functions instead of separate state
  const displayedYardSpots = useMemo(() => {
    if (!yardSpots) return [];
    const filtered = yardSpots.filter(spot => {
      if (filterZone && spot.zoneId !== filterZone) return false;
      if (filterStatus && spot.status !== filterStatus) return false;
      
      // Apply spot batch filter
      if (spotBatchFilter) {
        const batch = generateSpotBatches.find(b => b.label === spotBatchFilter);
        if (batch) {
          const match = spot.displayNumber.match(/\d+/);
          const spotNum = match ? parseInt(match[0], 10) : null;
          if (spotNum === null || spotNum < batch.start || spotNum > batch.end) {
            return false;
          }
        }
      }
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          spot.displayNumber.toLowerCase().includes(term) ||
          spot.driverName?.toLowerCase().includes(term) ||
          spot.trailerNumber?.toLowerCase().includes(term) ||
          spot.shipmentNumber?.toLowerCase().includes(term)
        );
      }
      return true;
    });
    
    // Deduplicate by spot ID
    const uniqueSpots = filtered.filter((spot, index, self) => 
      index === self.findIndex(s => s.id === spot.id)
    );
    
    return uniqueSpots;
  }, [yardSpots, filterZone, filterStatus, searchTerm, spotBatchFilter, generateSpotBatches]);


  

  // Add state for staging trailers
  const [stagingTrailers, setStagingTrailers] = useState<StagingTrailer[]>([]);
  const [loadingStagingTrailers, setLoadingStagingTrailers] = useState<boolean>(false);
  
  // Add state for zone-based trailers
  const [zoneTrailers, setZoneTrailers] = useState<ZoneTrailer[]>([]);
  const [loadingZoneTrailers, setLoadingZoneTrailers] = useState<boolean>(false);
  
  // Add state for moving trailer from staging to yard spot
  const [showMoveToYardSpotModal, setShowMoveToYardSpotModal] = useState<boolean>(false);
  const [selectedStagingTrailer, setSelectedStagingTrailer] = useState<StagingTrailer | null>(null);
  const [selectedYardSpotForMove, setSelectedYardSpotForMove] = useState<string>('');
  const [selectedYardSpotUuid, setSelectedYardSpotUuid] = useState<string>('');
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string | null>(null); // Zone filter within modal
  const [spotFilter, setSpotFilter] = useState<string>('');
  const [autoCompleteMoveToYardSpot, setAutoCompleteMoveToYardSpot] = useState<boolean>(false);
  const [assigningYardSpot, setAssigningYardSpot] = useState<boolean>(false);
  const [assignYardSpotError, setAssignYardSpotError] = useState<string | null>(null);
  
  // First, add state for the dock door assignment modal
  const [showMoveToDockModal, setShowMoveToDockModal] = useState<boolean>(false);
  const [selectedDockDoor, setSelectedDockDoor] = useState<string>('');
  const [dockFilter, setDockFilter] = useState<string>('');
  const [selectedShipmentPuid, setSelectedShipmentPuid] = useState<string>('');
  const [movingToDock, setMovingToDock] = useState<boolean>(false);
  const [moveToDockError, setMoveToDockError] = useState<string | null>(null);
  const [validatingShipment, setValidatingShipment] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [createdTask, setCreatedTask] = useState<any>(null);
  const [lockedSpots, setLockedSpots] = useState<Set<string>>(new Set());
  const [lockedZoneTrailers, setLockedZoneTrailers] = useState<Set<string>>(new Set());
  const [isMovingTrailer, setIsMovingTrailer] = useState<boolean>(false);
  
  // Add state for destination selection (yard spot or facility zone)
  const [selectedDestinationType, setSelectedDestinationType] = useState<string>('yard_spot');
  const [selectedFacilityZone, setSelectedFacilityZone] = useState<string>('');
  
  // Add state for zone trailer details modal
  const [showZoneTrailerDetailsModal, setShowZoneTrailerDetailsModal] = useState<boolean>(false);
  const [selectedZoneTrailer, setSelectedZoneTrailer] = useState<ZoneTrailer | null>(null);
  
  // Trailer notes states (for zone trailers)
  const [trailerNotesValue, setTrailerNotesValue] = useState<string>('');
  const [isEditingTrailerNotes, setIsEditingTrailerNotes] = useState<boolean>(false);
  const [savingTrailerNotes, setSavingTrailerNotes] = useState<boolean>(false);
  
  // Trailer notes states (for yard spot trailers)
  const [spotTrailerNotesValue, setSpotTrailerNotesValue] = useState<string>('');
  const [isEditingSpotTrailerNotes, setIsEditingSpotTrailerNotes] = useState<boolean>(false);
  const [savingSpotTrailerNotes, setSavingSpotTrailerNotes] = useState<boolean>(false);
  
  // Shipment editing state
  const [newShipmentReference, setNewShipmentReference] = useState<string>('');
  const [shipmentReferenceError, setShipmentReferenceError] = useState<string>('');
  const [isUpdatingShipment, setIsUpdatingShipment] = useState<boolean>(false);
  const [showRemoveShipmentConfirm, setShowRemoveShipmentConfirm] = useState<boolean>(false);
  const [isRemovingShipment, setIsRemovingShipment] = useState<boolean>(false);
  
  // Add state for task details modal
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState<boolean>(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState<any>(null);
  
  // Add state for unassigning trailer
  const [isUnassigningTrailer, setIsUnassigningTrailer] = useState<boolean>(false);
  
  // Add state for adding trailer to empty spot
  const [isAddingTrailer, setIsAddingTrailer] = useState<boolean>(false);
  const [addTrailerScac, setAddTrailerScac] = useState<string>('');
  const [addTrailerNumber, setAddTrailerNumber] = useState<string>('');
  const [addTrailerStatus, setAddTrailerStatus] = useState<'EMPTY' | 'LOADED'>('EMPTY');
  const [addTrailerError, setAddTrailerError] = useState<string | null>(null);
  const [showCreateAnywayButton, setShowCreateAnywayButton] = useState<boolean>(false);
  const [trailerSearchInput, setTrailerSearchInput] = useState<string>('');
  const [addTrailerShipmentNumber, setAddTrailerShipmentNumber] = useState<string>('');
  const [selectedExistingTrailer, setSelectedExistingTrailer] = useState<ExistingTrailer | null>(null);
  const [existingTrailersList, setExistingTrailersList] = useState<ExistingTrailer[]>([]);
  
  // Add state for active dock doors with zone information
  const [activeDockDoors, setActiveDockDoors] = useState<Array<{door: string, dockUuid: string, zoneUuid: string, zoneName: string, hasMoveFromTask?: boolean, facilityId?: string}>>([]);

  // Move-modal destinations: the whole campus in a multi-facility campus, else the page's own data.
  const moveDestinations = useModalDestinations({
    sourceFacilityId: selectedFacility?.id,
    enabled: showMoveToYardSpotModal || showMoveToDockModal,
    fallback: {
      yardSpots,
      zones: allZones.length > 0 ? allZones : zones,
      facilityZones: allFacilityZones.length > 0 ? allFacilityZones : facilityZones,
      dockDoors: activeDockDoors,
      lockedSpots: taskBasedLockedSpots,
    },
  });
  const campusFilterProps = (dockOnly: boolean) =>
    moveDestinations.campusMode
      ? {
          facilities: dockOnly ? moveDestinations.dockFacilities : moveDestinations.facilities,
          value: moveDestinations.facilityFilter,
          onChange: moveDestinations.setFacilityFilter,
        }
      : undefined;
  const [loadingDockDoors, setLoadingDockDoors] = useState<boolean>(false);

  // Yard Check Mode State - with localStorage persistence
  const YARD_CHECK_STORAGE_KEY = 'yard_check_active';
  
  const [isYardCheckMode, setIsYardCheckMode] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(YARD_CHECK_STORAGE_KEY);
      return stored ? JSON.parse(stored).isActive : false;
    } catch {
      return false;
    }
  });

  // Track verified spots during yard check
  const [verifiedSpots, setVerifiedSpots] = useState<Map<string, {
    yard_spot_id: string;
    spot_number: string;
    trailer_id: string | null;
    trailer_number: string | null;
    carrier_name: string | null;
    shipment_id: string | null;
    shipment_number: string | null;
    driver_id: string | null;
    timestamp: number;
  }>>(() => {
    try {
      const stored = localStorage.getItem(YARD_CHECK_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.verifiedSpots) {
          return new Map(Object.entries(data.verifiedSpots));
        }
      }
      return new Map();
    } catch {
      return new Map();
    }
  });

  // Track visited ranges (to only send modification once per range)
  const [visitedRanges, setVisitedRanges] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(YARD_CHECK_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.visitedRanges) {
          return new Set(data.visitedRanges);
        }
      }
      return new Set();
    } catch {
      return new Set();
    }
  });

  // Auto-select facility if only one exists
  useEffect(() => {
    if (facilities.length === 1 && !selectedFacility) {
      setSelectedFacility(facilities[0]);
    }
  }, [facilities, selectedFacility]);

  // Get zones filtered by SCACs if specified, with match counts per SCAC
  const filteredZonesBySCAC = useMemo(() => {
    if (scacFilters.length === 0) return null; // No filter applied
    
    // Get all zones that have trailers with matching SCACs and count matches per SCAC
    const zonesWithSCAC = new Map<string, Map<string, number>>(); // zoneId -> Map<scac, count>
    
    yardSpots.forEach(spot => {
      if (spot.carrier_name) {
        const carrierUpper = spot.carrier_name.toUpperCase();
        // Check if carrier matches any of the selected SCACs
        scacFilters.forEach(scac => {
          if (carrierUpper.includes(scac)) {
            if (!zonesWithSCAC.has(spot.zoneId)) {
              zonesWithSCAC.set(spot.zoneId, new Map());
            }
            const scacMap = zonesWithSCAC.get(spot.zoneId)!;
            scacMap.set(scac, (scacMap.get(scac) || 0) + 1);
          }
        });
      }
    });
    
    return zonesWithSCAC;
  }, [scacFilters, yardSpots]);

  // Get unique SCACs for autocomplete
  const uniqueSCACs = useMemo(() => {
    const scacs = new Set<string>();
    yardSpots.forEach(spot => {
      if (spot.carrier_name) {
        scacs.add(spot.carrier_name.trim());
      }
    });
    return Array.from(scacs).sort();
  }, [yardSpots]);

  // The zones a yard check can cover, in the order "Next zone" drives them:
  // the Zone dropdown's list (no Staging Area, narrowed by SCAC) by name.
  const orderedCheckZones = useMemo(() =>
    (allZones.length > 0 ? allZones : zones)
      .filter(zone => zone.name !== "Staging Area")
      .filter(zone => !filteredZonesBySCAC || filteredZonesBySCAC.has(zone.id))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [allZones, zones, filteredZonesBySCAC]
  );

  const zoneName = (zoneId: string | null) =>
    (allZones.length > 0 ? allZones : zones).find(z => z.id === zoneId)?.name;
  
  const [yardCheckId, setYardCheckId] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(YARD_CHECK_STORAGE_KEY);
      return stored ? JSON.parse(stored).zoneId : null;
    } catch {
      return null;
    }
  });
  
  const [yardCheckStartTime, setYardCheckStartTime] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem(YARD_CHECK_STORAGE_KEY);
      return stored ? JSON.parse(stored).startTime : null;
    } catch {
      return null;
    }
  });
  
  const [yardCheckSpots, setYardCheckSpots] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(YARD_CHECK_STORAGE_KEY);
      return stored ? JSON.parse(stored).spotIds : [];
    } catch {
      return [];
    }
  });

  // The run the active zone check belongs to (null for a check started before runs existed)
  const [yardCheckRun, setYardCheckRun] = useState<YardCheckRun | null>(loadYardCheckRun);

  // Sent with every move so the server knows it was made during this check.
  const currentYardCheck = () => yardCheckContext(isYardCheckMode, yardCheckId, yardCheckRun?.runUuid);
  const [advancingZone, setAdvancingZone] = useState<boolean>(false);

  // Every zone on the campus a check can move to, by facility then zone name.
  // Falls back to the page's own zones until the campus has loaded.
  const campusYard = useCampusDestinations(selectedFacility?.id, !!selectedFacility);

  const campusCheckZones = useMemo<CheckZone[]>(() => {
    if (campusYard.facilityZones.length === 0) {
      return orderedCheckZones.map(zone => ({
        id: zone.id,
        name: zone.name,
        facilityId: String(selectedFacility?.id ?? ''),
        facilityName: selectedFacility?.name || '',
        facilityAlias: facilities.find(f => f.id === selectedFacility?.id)?.facility_alias ?? selectedFacility?.facility_alias,
        facilityLabel: selectedFacilityLabel
      }));
    }

    return campusYard.facilityZones
      .filter(zone => !isUncheckedZone(zone.facility_zone_name))
      .map(zone => {
        const facility = facilityLabelById(campusYard.facilities, String(zone.facilityId));
        return {
          id: zone.facility_zone_uuid,
          name: zone.facility_zone_name,
          facilityId: String(zone.facilityId),
          facilityName: zone.facilityName,
          facilityAlias: facility?.facility_alias,
          facilityLabel: facilityDisplayName(facility) || zone.facilityName
        };
      })
      .sort((a, b) => a.facilityLabel.localeCompare(b.facilityLabel) || a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [campusYard.facilityZones, campusYard.facilities, orderedCheckZones, facilities, selectedFacility, selectedFacilityLabel]);

  const campusIsMultiFacility = new Set(campusCheckZones.map(zone => zone.facilityId)).size > 1;
  const checkZoneLabel = (zone: CheckZone) =>
    campusIsMultiFacility ? `${zone.name} (${zone.facilityLabel})` : zone.name;

  const checkZoneGroups = useMemo<CheckZoneGroup[]>(() => {
    const groups = new Map<string, CheckZoneGroup>();
    campusCheckZones.forEach(zone => {
      const group = groups.get(zone.facilityId) || { label: zone.facilityLabel, options: [] };
      group.options.push({
        value: zone.id,
        label: zone.id === yardCheckId ? `${zone.name} (current)` : zone.name,
        zone
      });
      groups.set(zone.facilityId, group);
    });
    return Array.from(groups.values());
  }, [campusCheckZones, yardCheckId]);

  const nextCheckZone = useMemo(() => {
    const index = campusCheckZones.findIndex(zone => zone.id === yardCheckId);
    return index >= 0 ? campusCheckZones[index + 1] || null : null;
  }, [campusCheckZones, yardCheckId]);

  // The facility whose zone is being checked; audits are filed under it.
  const checkFacilityId = yardCheckRun?.facilityId || selectedFacility?.id?.toString() || "1";

  // Keep the page on the checked zone's facility, which may be a campus
  // facility outside the user's own list (useYardData resets those on load).
  useEffect(() => {
    const facilityId = yardCheckRun?.facilityId;
    if (!isYardCheckMode || !facilityId || String(selectedFacility?.id) === facilityId) return;

    const zone = campusCheckZones.find(z => z.facilityId === facilityId);
    const facility = facilities.find(f => String(f.id) === facilityId);
    const name = zone?.facilityName || facility?.name || `Facility ${facilityId}`;
    setSelectedFacility({ id: Number(facilityId), name, facility_alias: zone?.facilityAlias ?? facility?.facility_alias });
  }, [isYardCheckMode, yardCheckRun?.facilityId, selectedFacility?.id, campusCheckZones, facilities, setSelectedFacility]);

  // Spotter truck: required at a campus with RTLS, saved on this device
  const [spotterContext, setSpotterContext] = useState<YardCheckSpotterContext | null>(null);
  const [spotterContextError, setSpotterContextError] = useState<boolean>(false);
  const [spotterTruck, setSpotterTruck] = useState<StoredSpotterTruck | null>(null);
  const [spotterDialogOpen, setSpotterDialogOpen] = useState<boolean>(false);
  const spotterTruckRequired = !!spotterContext?.requires_spotter_truck;
  const spotterTruckMissing = spotterTruckRequired && !spotterTruck;

  // Read inside the context load without reloading it when a check starts or ends.
  const isYardCheckModeRef = useRef(isYardCheckMode);
  isYardCheckModeRef.current = isYardCheckMode;
  const selectedFacilityId = selectedFacility?.id?.toString() || null;

  useEffect(() => {
    if (!selectedFacilityId) return;
    let cancelled = false;

    setSpotterContext(null);
    setSpotterContextError(false);

    getYardCheckSpotterContext(selectedFacilityId)
      .then(context => {
        if (cancelled) return;
        const stored = loadSpotterTruck(context.account_id);
        setSpotterContext(context);
        setSpotterTruck(stored);
        // The first yard check on this device asks for the truck up front.
        if (context.requires_spotter_truck && !stored && !isYardCheckModeRef.current) {
          setSpotterDialogOpen(true);
        }
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load spotter trucks', error);
        setSpotterContextError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFacilityId]);

  // Any edit overwrites the truck saved on this device.
  const handleSpotterTruckSaved = (truck: SpotterTruck) => {
    const stored: StoredSpotterTruck = {
      uuid: truck.uuid,
      name: truck.name,
      ziggy_uuid: truck.ziggy_uuid,
      account_id: String(truck.account_id)
    };
    saveSpotterTruck(stored);
    setSpotterTruck(stored);
    setSpotterDialogOpen(false);

    setSpotterContext(context => context && !context.spotter_trucks.some(t => t.uuid === truck.uuid)
      ? { ...context, spotter_trucks: [...context.spotter_trucks, truck].sort((a, b) => a.name.localeCompare(b.name)) }
      : context);
  };
  
  
  
  // Hydrate yard check state on mount (restore zone and spot range)
  useEffect(() => {
    if (isYardCheckMode && yardCheckId) {
      try {
        const stored = localStorage.getItem(YARD_CHECK_STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          
          // Restore zone filter
          if (data.zoneId && filterZone !== data.zoneId) {
            setFilterZone(data.zoneId);
          }
          
          // Restore spot batch filter (the range they were on)
          if (data.spotBatchFilter) {
            setSpotBatchFilter(data.spotBatchFilter);
          }
        }
      } catch (error) {
        console.error('Error hydrating yard check state:', error);
      }
    }
  }, []); // Run only on mount

  // Background refresh during yard check mode
  useEffect(() => {
    if (!isYardCheckMode) return;
    
    // Set up interval to refresh data every 30 seconds
    const intervalId = setInterval(() => {
      console.log('Yard check background refresh...');
      // Use silent refresh to avoid showing loading state
      refreshData(filterZone || undefined, true);
    }, 30000); // 30 seconds
    
    // Cleanup interval on unmount or when yard check mode ends
    return () => clearInterval(intervalId);
  }, [isYardCheckMode, refreshData, filterZone]);

  // Warn user before leaving page if yard check is active
  useEffect(() => {
    if (!isYardCheckMode) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Chrome requires returnValue to be set
      return ''; // Some browsers show this message
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isYardCheckMode]);

  // Save dimensions and scroll position
  useLayoutEffect(() => {
    // Save initial dimensions of containers
    if (spotsContainerRef.current && !spotsHeight) {
      setSpotsHeight(spotsContainerRef.current.offsetHeight);
    }
    
    // Save scroll position continually
    const handleScroll = () => {
      if (!isRestoringScroll) {
        scrollY.current = window.scrollY;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [spotsHeight, isRestoringScroll]);
  
  // Restore scroll position after state updates
  useLayoutEffect(() => {
    if (scrollY.current > 0 && !isRestoringScroll) {
      setIsRestoringScroll(true);
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY.current);
        // Wait a bit more for any layout shifts to settle
        setTimeout(() => setIsRestoringScroll(false), 50);
      });
    }
  }, [yardSpots, stagingTrailers]);




  
  // Helper to save yard check state to localStorage
  const saveYardCheckState = useCallback((state: {
    isActive: boolean;
    zoneId: string | null;
    startTime: number | null;
    spotIds: string[];
    verifiedSpots?: Map<string, any>;
    spotBatchFilter?: string | null;
    visitedRanges?: Set<string>;
  }) => {
    try {
      const stateToSave = {
        ...state,
        verifiedSpots: state.verifiedSpots ? Object.fromEntries(state.verifiedSpots) : {},
        spotBatchFilter: state.spotBatchFilter || null,
        visitedRanges: state.visitedRanges ? Array.from(state.visitedRanges) : []
      };
      localStorage.setItem(YARD_CHECK_STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Failed to save yard check state:', error);
    }
  }, [YARD_CHECK_STORAGE_KEY]);

  // Handle verifying a spot during yard check (no mutation, just local state)
  const handleVerifySpot = (spot: LocalYardSpot) => {
    if (!isYardCheckMode || !yardCheckId) return;

    const newVerifiedSpots = new Map(verifiedSpots);
    
    // Toggle verification - if already verified, unverify it
    if (newVerifiedSpots.has(spot.id)) {
      newVerifiedSpots.delete(spot.id);
    } else {
      // Add verification - store IDs instead of display values
      const verificationData = {
        yard_spot_id: spot.id,
        spot_number: spot.displayNumber,
        trailer_id: spot.trailerId || null,
        trailer_number: spot.trailerNumber || null,
        carrier_name: spot.carrier_name || null,
        shipment_id: spot.bol_id || null,
        shipment_number: spot.shipmentNumber || null,
        driver_id: spot.driverId || null,
        timestamp: Date.now()
      };
      newVerifiedSpots.set(spot.id, verificationData);
    }
    
    setVerifiedSpots(newVerifiedSpots);

    // Save to localStorage (no API call)
    saveYardCheckState({
      isActive: true,
      zoneId: yardCheckId,
      startTime: yardCheckStartTime,
      spotIds: yardCheckSpots,
      verifiedSpots: newVerifiedSpots,
      spotBatchFilter
    });
  };

  // Yard Check Handlers

  // Starts checking one zone of a run: records its started event, then enters
  // check mode on it. Returns false if the event could not be recorded.
  const startZoneCheck = async (
    zone: { id: string; name: string; facilityId: string },
    batchLabel: string | null,
    spots: { id: string; displayNumber: string }[],
    run: YardCheckRun
  ): Promise<boolean> => {
    const zoneId = zone.id;
    run = { ...run, facilityId: zone.facilityId };
    const startTime = Date.now();
    const spotIds = spots.map(s => s.id);

    try {
      await createAudit({
        action: 'yms_yard_check_started',
        model: 'facility_zone',
        model_id: zoneId, // Use the zone UUID as model_id
        facility_id: zone.facilityId,
        params: {
          zone_name: zone.name || "Unknown",
          spot_range: batchLabel || "All spots",
          total_spots: spots.length,
          spots: spots.map(s => s.displayNumber).join(', '),
          yard_check_run_uuid: run.runUuid,
          ...(run.truck
            ? {
                spotter_truck_uuid: run.truck.uuid,
                spotter_truck_name: run.truck.name,
                spotter_truck_ziggy_uuid: run.truck.ziggy_uuid
              }
            : {})
        }
      });
    } catch (error) {
      console.error("Failed to start yard check audit", error);
      return false;
    }

    // Update state
    setYardCheckId(zoneId);
    setYardCheckStartTime(startTime);
    setYardCheckSpots(spotIds);
    setIsYardCheckMode(true);
    setVerifiedSpots(new Map()); // Reset verified spots for new check
    setYardCheckRun(run);
    saveYardCheckRun(run);

    // Mark starting range as visited (so we don't log it as a modification)
    const initialVisitedRanges = new Set<string>();
    if (batchLabel) {
      initialVisitedRanges.add(batchLabel);
    }
    setVisitedRanges(initialVisitedRanges);

    // Persist to localStorage
    saveYardCheckState({
      isActive: true,
      zoneId,
      startTime,
      spotIds,
      verifiedSpots: new Map(),
      spotBatchFilter: batchLabel,
      visitedRanges: initialVisitedRanges
    });
    return true;
  };

  const handleStartYardCheck = async () => {
    if (!filterZone) {
      alert("Please select a zone to start yard check.");
      return;
    }

    if (spotterTruckMissing) {
      setSpotterDialogOpen(true);
      return;
    }

    const run: YardCheckRun = {
      runUuid: newRunUuid(),
      truck: spotterTruckRequired ? spotterTruck : null
    };

    const zone = {
      id: filterZone,
      name: zoneName(filterZone) || "Unknown",
      facilityId: selectedFacility?.id?.toString() || "1"
    };

    if (!(await startZoneCheck(zone, spotBatchFilter, displayedYardSpots, run))) {
      alert("Failed to start yard check. Please try again.");
    }
  };

  // Records the end of the zone being checked, with its verified spots.
  // Throws if the event could not be recorded.
  const endZoneCheck = async () => {
    if (!yardCheckId || !yardCheckStartTime) return;

    const durationMinutes = Math.round((Date.now() - yardCheckStartTime) / 60000);

    // Prepare verified spots data
    const verifiedSpotsArray = Array.from(verifiedSpots.values());

    // Calculate total spots based on all visited ranges
    let totalSpotsChecked = 0;

    visitedRanges.forEach(range => {
      const batch = generateSpotBatches.find(b => b.label === range);
      if (batch) {
        totalSpotsChecked += spotsInBatch(yardSpots, yardCheckId, batch).length;
      }
    });

    // If no ranges were tracked (shouldn't happen), fall back to yardCheckSpots.length
    if (totalSpotsChecked === 0) {
      totalSpotsChecked = yardCheckSpots.length;
    }

    await createAudit({
      action: 'yms_yard_check_ended',
      model: 'facility_zone',
      model_id: yardCheckId,
      facility_id: checkFacilityId,
      params: {
        duration_minutes: durationMinutes,
        total_spots: totalSpotsChecked,
        verified_spots_count: verifiedSpots.size,
        verified_spots: verifiedSpotsArray
      }
    });
  };

  // Leaves check mode, forgetting the check, its run and the zone filters.
  const resetYardCheck = () => {
    setIsYardCheckMode(false);
    setYardCheckId(null);
    setYardCheckStartTime(null);
    setYardCheckSpots([]);
    setVerifiedSpots(new Map());
    setVisitedRanges(new Set());
    setYardCheckRun(null);
    saveYardCheckRun(null);

    // Clear filters
    setFilterZone(null);
    setSpotBatchFilter(null);

    // Clear localStorage
    localStorage.removeItem(YARD_CHECK_STORAGE_KEY);
    try {
      localStorage.removeItem(STORAGE_KEYS.ZONE_FILTER);
      localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
    } catch (error) {
      console.error('Error clearing filter storage:', error);
    }
  };

  const handleEndYardCheck = async () => {
    if (!yardCheckId || !yardCheckStartTime) return;

    // Confirm before ending
    const confirmed = window.confirm(
      `Are you sure you want to end the yard check for ${zoneName(yardCheckId) || 'Unknown Zone'}?`
    );

    if (!confirmed) return;

    try {
      await endZoneCheck();
      resetYardCheck();
    } catch (error) {
      console.error("Failed to end yard check audit", error);
      alert("Failed to end yard check. Please try again.");
    }
  };

  // Finishes the zone being checked and starts another in the same run: the
  // next one ("Next zone") or any campus zone picked by hand.
  const handleSwitchZone = async (target: CheckZone | null) => {
    if (!target || !yardCheckId || !yardCheckStartTime || advancingZone) return;
    if (target.id === yardCheckId) return;

    const currentName = zoneName(yardCheckId) || 'this zone';

    if (!window.confirm(`Finish ${currentName} and start ${checkZoneLabel(target)}?`)) return;

    setAdvancingZone(true);
    try {
      try {
        await endZoneCheck();
      } catch (error) {
        console.error("Failed to end yard check audit", error);
        alert(`Failed to finish ${currentName}. Please try again.`);
        return;
      }

      // The page holds only the selected facility's spots; another facility's
      // come from the campus destinations.
      const sameFacility = target.facilityId === String(selectedFacility?.id);
      const spots: CheckSpot[] = sameFacility
        ? yardSpots
        : campusYard.yardSpots
            .filter(spot => String(spot.facilityId) === target.facilityId)
            .map(spot => ({
              id: spot.yard_spot_uuid,
              zoneId: spot.facility_zone_uuid,
              displayNumber: spot.yard_spot_name
            }));

      const firstBatch = spotBatchesForZone(spots, target.id)[0] || null;
      const run: YardCheckRun = yardCheckRun || {
        runUuid: newRunUuid(),
        truck: spotterTruckRequired ? spotterTruck : null
      };

      const started = await startZoneCheck(
        target,
        firstBatch?.label || null,
        spotsInBatch(spots, target.id, firstBatch),
        run
      );

      if (!started) {
        // Leave check mode but land on that zone, so Start Yard Check is one tap away.
        resetYardCheck();
        alert(`${currentName} is finished, but starting ${checkZoneLabel(target)} failed. Press Start Yard Check to continue.`);
      }

      if (!sameFacility) {
        setSelectedFacility({ id: Number(target.facilityId), name: target.facilityName, facility_alias: target.facilityAlias });
      }
      setFilterZone(target.id);
      setSpotBatchFilter(firstBatch?.label || null);
      try {
        localStorage.setItem(STORAGE_KEYS.ZONE_FILTER, target.id);
        if (firstBatch) {
          localStorage.setItem(STORAGE_KEYS.SPOT_BATCH, firstBatch.label);
        } else {
          localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
        }
      } catch (error) {
        console.error('Error saving zone filter preference:', error);
      }
    } finally {
      setAdvancingZone(false);
    }
  };

  const handleYardCheckNext = async () => {
     if (!yardCheckId || generateSpotBatches.length === 0) return;
     
     // Find current batch index
     const currentBatchIndex = spotBatchFilter 
       ? generateSpotBatches.findIndex(b => b.label === spotBatchFilter)
       : -1;
     
     // Check if there's a next batch
     if (currentBatchIndex < generateSpotBatches.length - 1) {
       const nextBatch = generateSpotBatches[currentBatchIndex + 1];
       
       // Check if this is a new range (not visited before)
       const isNewRange = !visitedRanges.has(nextBatch.label);
       
       // Update to next batch
       setSpotBatchFilter(nextBatch.label);
       
       // Mark this range as visited
       const newVisitedRanges = new Set(visitedRanges);
       newVisitedRanges.add(nextBatch.label);
       setVisitedRanges(newVisitedRanges);
       
       // Save to localStorage
       try {
         localStorage.setItem(STORAGE_KEYS.SPOT_BATCH, nextBatch.label);
       } catch (error) {
         console.error('Error saving spot batch filter:', error);
       }
       
       // Update yard check state
       saveYardCheckState({
         isActive: true,
         zoneId: yardCheckId,
         startTime: yardCheckStartTime,
         spotIds: yardCheckSpots,
         verifiedSpots,
         spotBatchFilter: nextBatch.label,
         visitedRanges: newVisitedRanges
       });
       
       // Only log range change if this is a NEW range
       if (isNewRange) {
         try {
           await createAudit({
             action: 'yms_yard_check_modified',
             model: 'facility_zone',
             model_id: yardCheckId,
             facility_id: checkFacilityId,
             params: {
               previous_spot_range: spotBatchFilter || "All spots",
               new_spot_range: nextBatch.label,
               spots_in_range: displayedYardSpots.length
             }
           });
         } catch (error) {
           console.error("Failed to log yard check range change", error);
         }
       }
     }
  };

  const handleYardCheckPrev = async () => {
     if (!yardCheckId || generateSpotBatches.length === 0) return;
     
     // Find current batch index
     const currentBatchIndex = spotBatchFilter 
       ? generateSpotBatches.findIndex(b => b.label === spotBatchFilter)
       : -1;
     
     // Check if there's a previous batch
     if (currentBatchIndex > 0) {
       const prevBatch = generateSpotBatches[currentBatchIndex - 1];
       
       // Check if this is a new range (not visited before)
       const isNewRange = !visitedRanges.has(prevBatch.label);
       
       // Update to previous batch
       setSpotBatchFilter(prevBatch.label);
       
       // Mark this range as visited
       const newVisitedRanges = new Set(visitedRanges);
       newVisitedRanges.add(prevBatch.label);
       setVisitedRanges(newVisitedRanges);
       
       // Save to localStorage
       try {
         localStorage.setItem(STORAGE_KEYS.SPOT_BATCH, prevBatch.label);
       } catch (error) {
         console.error('Error saving spot batch filter:', error);
       }
       
       // Update yard check state
       saveYardCheckState({
         isActive: true,
         zoneId: yardCheckId,
         startTime: yardCheckStartTime,
         spotIds: yardCheckSpots,
         verifiedSpots,
         spotBatchFilter: prevBatch.label,
         visitedRanges: newVisitedRanges
       });
       
       // Only log range change if this is a NEW range
       if (isNewRange) {
         try {
           await createAudit({
             action: 'yms_yard_check_modified',
             model: 'facility_zone',
             model_id: yardCheckId,
             facility_id: checkFacilityId,
             params: {
               previous_spot_range: spotBatchFilter || "All spots",
               new_spot_range: prevBatch.label,
               spots_in_range: displayedYardSpots.length
             }
           });
         } catch (error) {
           console.error("Failed to log yard check range change", error);
         }
       }
     }
  };

  // Handler functions
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  

  


  // Unified modal handlers
  const handleShowUnifiedDetails = (item: LocalYardSpot | ZoneTrailer, type: 'yard_spot' | 'zone_trailer') => {
    const unifiedItem = type === 'yard_spot' 
      ? createUnifiedItemFromSpot(item as LocalYardSpot, zones)
      : createUnifiedItemFromZoneTrailer(item as ZoneTrailer, zones);
    
    setSelectedUnifiedItem(unifiedItem);
    setShowUnifiedDetailsModal(true);
    
    // Ensure other modals are closed
    setShowMoveToDockModal(false);
    setShowMoveToYardSpotModal(false);
    
    // Set up notes for editing if available
    if (unifiedItem.active_lineage?.notes) {
      setSpotTrailerNotesValue(unifiedItem.active_lineage.notes);
    }
    
    // Set selectedSpot for yard spots so handleSaveSpotTrailerNotes works
    if (type === 'yard_spot') {
      setSelectedSpot(item as LocalYardSpot);
    }
  };

  const handleCloseUnifiedModal = () => {
    setShowUnifiedDetailsModal(false);
    setSelectedUnifiedItem(null);
    setSelectedSpot(null);
    // Reset add trailer form
    setAddTrailerScac('');
    setAddTrailerNumber('');
    setAddTrailerStatus('EMPTY');
    setAddTrailerError(null);
    setShowCreateAnywayButton(false);
    setTrailerSearchInput('');
    setAddTrailerShipmentNumber('');
    setSelectedExistingTrailer(null);
  };

  const handleCloseQuickAddModal = () => {
    setShowQuickAddModal(false);
    setQuickAddSpot(null);
    setQuickAddScac('');
    setQuickAddNumber('');
    setQuickAddStatus('EMPTY');
    setQuickAddSearchInput('');
    setQuickAddSelectedExisting(null);
    setQuickAddError(null);
    setShowQuickAddCreateAnywayButton(false);
    setIsQuickAdding(false);
  };

  const handleQuickAddTrailer = async (forceCreate: boolean = false) => {
    if (!quickAddSpot) return;
    
    if (!quickAddScac.trim() || !quickAddNumber.trim()) {
      setQuickAddError('Please enter both SCAC and trailer number');
      return;
    }
    
    setIsQuickAdding(true);
    setQuickAddError(null);
    
    try {
      // Check if trailer already exists in the yard spots or zones (but NOT warehouse)
      const existingTrailers = await getAllExistingTrailers();
      const existingTrailer = existingTrailers.find((t: any) => 
        t.scac.toLowerCase() === quickAddScac.trim().toLowerCase() &&
        t.trailerNumber.toLowerCase() === quickAddNumber.trim().toLowerCase()
      );
      
      if (existingTrailer) {
        // Trailer exists in yard or zone - create movement task to move it to this spot
        await createMovementTask({
          trailer_uuid: existingTrailer.trailerUuid,
          movement_type: 'yard_to_yard',
          facility_id: selectedFacility?.id?.toString() || "1",
          yard_spot_uuid: quickAddSpot.id,
          facility_zone_uuid: quickAddSpot.zoneId,
          force: false,
          auto_complete: true,
          shipment_id: existingTrailer.shipmentId || null,
          yard_check: currentYardCheck()
        });
        
        alert(`Trailer ${quickAddNumber} moved to ${quickAddSpot.displayNumber}`);
        
      } else {
        // Trailer doesn't exist in yard/zones - create new trailer at this spot
        const trailerData = await addTrailer({
          scac: quickAddScac.trim(),
          trailerNumber: quickAddNumber.trim(),
          facilityId: selectedFacility?.id?.toString() || "1",
          status: quickAddStatus,
          yardSpotUuid: quickAddSpot.id,
          facilityZoneUuid: quickAddSpot.zoneId,
          notes: null,
          force_create_trailer_if_active: forceCreate
        });
        
        if (!trailerData?.success) {
          throw new Error(trailerData?.errors?.join(', ') || 'Failed to add trailer');
        }
        
        alert(`Trailer ${quickAddNumber} added to ${quickAddSpot.displayNumber}`);
      }
      
      // Reset form and refresh data
      handleCloseQuickAddModal();
      await refreshData(filterZone || undefined, true);
      
    } catch (error: any) {
      console.error('Error adding trailer to spot:', error);
      const isTimeout = error.name === 'AbortError' || error.message?.includes('timeout');

      if (isTimeout && quickAddSpot) {
        // Connection died but server may have succeeded — do a lightweight zone refresh
        await refreshZone(quickAddSpot.zoneId);
        handleCloseQuickAddModal();
      } else {
        const errorMessage = error.message || 'Failed to add trailer';
        setQuickAddError(errorMessage);

        // Check if error contains "is already active"
        if (errorMessage.toLowerCase().includes('is already active')) {
          setShowQuickAddCreateAnywayButton(true);
        }
      }
    } finally {
      setIsQuickAdding(false);
    }
  };

  // Handle create anyway for quick add (force create despite "is already active" error)
  const handleQuickAddCreateAnyway = () => {
    setShowQuickAddCreateAnywayButton(false);
    handleQuickAddTrailer(true);
  };

  const handleClearExistingTrailer = () => {
    setSelectedExistingTrailer(null);
    setAddTrailerScac('');
    setAddTrailerNumber('');
    setAddTrailerStatus('EMPTY');
    setAddTrailerShipmentNumber('');
    setTrailerSearchInput('');
    setAddTrailerError(null);
    setShowCreateAnywayButton(false);
  };

  const handleZoneManageClose = () => {
    setShowZoneManageModal(false);
  };
  
  const handleZoneEditClose = () => {
    setShowZoneEditModal(false);
    setEditingZone(null);
  };
  
  const handleShowZoneEdit = (zone: Zone) => {
    setEditingZone({ ...zone });
    setShowZoneEditModal(true);
  };
  
  const handleAddNewZone = () => {
    if (!newZoneName.trim()) return;
    
    const newZone: Zone = {
      id: `zone-${Date.now()}`, // Generate temp ID
      name: newZoneName,
      color: newZoneColor
    };
    
    // TODO: Implement API call to add zone
    console.log('Add new zone:', newZone);
    
    // Reset form
    setNewZoneName('');
    setNewZoneColor(getRandomColor());
  };
  
  const handleSaveZone = () => {
    if (!editingZone) return;
    
    // TODO: Implement API call to save zone
    console.log('Save zone:', editingZone);
    setShowZoneEditModal(false);
  };
  
  const handleDeleteZone = (zoneId: string) => {
    // TODO: Implement API call to delete zone
    console.log('Delete zone:', zoneId);
  };

  // Group zone trailers by zone - memoized to prevent recalculation
  const trailersByZone = useMemo(() => {
    // Show all zones, not just ones with trailers
    return zones
      .sort((a, b) => a.name.localeCompare(b.name)) // Sort zones alphabetically
      .map(zone => ({
        zone: {
          ...zone,
          color: zone.color || '#2196F3' // Default to blue if no color is defined
        },
        trailers: showUnallocatedAssets 
          ? zoneTrailers.filter(trailer => trailer.zone_id === zone.id)
              .filter(trailer => {
                if (searchTerm) {
                  const term = searchTerm.toLowerCase();
                  return (
                    trailer.trailer_number?.toLowerCase().includes(term) ||
                    trailer.driver_name?.toLowerCase().includes(term) ||
                    trailer.puid?.toLowerCase().includes(term) ||
                    trailer.carrier_name?.toLowerCase().includes(term)
                  );
                }
                return true;
              })
          : [] // Hide zone trailers when showUnallocatedAssets is false
      }));
    // Don't filter out empty zones - show all zones including empty ones
  }, [zoneTrailers, zones, searchTerm, showUnallocatedAssets]);



  
  // Update the handleEndAssignment function to use localModifications

  // Helper function to update spot to available
  const updateSpotToAvailable = (spotId: string) => {
    console.log(`Clearing yard spot ${spotId}, removing carrier_name and other data`);
    
    // Update local modifications
    setLocalModifications(prev => ({
      ...prev,
      [spotId]: { 
        status: 'available' as Status, 
        driverId: null, 
        driverName: null, 
        trailerId: null, 
        trailerNumber: null, 
        shipmentNumber: null,
        carrier_name: null // Explicitly clear carrier_name
      }
    }));
    
    // Update the selectedSpot if it's currently selected
    if (selectedSpot && selectedSpot.id === spotId) {
      setSelectedSpot({
        ...selectedSpot,
        status: 'available' as Status, 
        driverId: null, 
        driverName: null, 
        trailerId: null, 
        trailerNumber: null, 
        shipmentNumber: null,
        carrier_name: null // Explicitly clear carrier_name
      });
    }
    
    console.log(`Ended assignment for spot ${spotId}`);
  };
  


  // Add handleMoveToYardSpot function to open the modal
  const handleMoveToYardSpot = (trailer: StagingTrailer) => {
    setSelectedStagingTrailer(trailer);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setSelectedZoneFilter(null);
    setSpotFilter('');
    setAssignYardSpotError(null);
    // Pre-populate shipment reference from the trailer's shipment_number field
    setSelectedShipmentPuid(trailer.shipment_number || '');
    setShowMoveToYardSpotModal(true);
  };

  // Add handleCloseMoveToYardSpotModal function to close the modal
  const handleCloseMoveToYardSpotModal = () => {
    setShowMoveToYardSpotModal(false);
    setSelectedStagingTrailer(null);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setSelectedZoneFilter(null);
    setSpotFilter('');
    setAssignYardSpotError(null);
    setSelectedShipmentPuid('');
    setSelectedDestinationType('yard_spot');
    setSelectedFacilityZone('');
    setAutoCompleteMoveToYardSpot(false); // Reset auto-complete checkbox
  };

  // Update handleAssignYardSpot to use localModifications without triggering unnecessary re-renders
  const handleAssignYardSpot = async () => {
    if (!selectedStagingTrailer) {
      setAssignYardSpotError('No trailer selected');
      return;
    }
    
    if (selectedDestinationType === 'yard_spot' && !selectedYardSpotForMove) {
      setAssignYardSpotError('Please select a yard spot');
      return;
    }
    
    if (selectedDestinationType === 'facility_zone' && !selectedFacilityZone) {
      setAssignYardSpotError('Please select a facility zone');
      return;
    }

    setAssigningYardSpot(true);
    setAssignYardSpotError(null);
    
    let shipmentId: string | null = null;

    // Auto-populate from the selected trailer's existing shipment relationship
    if (selectedUnifiedItem?.type === 'zone_trailer') {
      const zt = selectedUnifiedItem.originalData as ZoneTrailer;
      shipmentId = zt.shipment_id || null;
    } else if (selectedUnifiedItem?.type === 'yard_spot') {
      const spot = selectedUnifiedItem.originalData as LocalYardSpot;
      shipmentId = spot.bol_id || null;
    }

    // Override with user-typed reference if provided
    if (selectedShipmentPuid && selectedShipmentPuid.trim()) {
      setValidatingShipment(true);
      
      try {
        // Validate that the shipment reference number exists
        const shipment: { id: string } | null = await validateShipmentReference(
          selectedShipmentPuid.trim(),
          selectedFacility?.id?.toString() || "1",
          // The trailer being moved: the modal's openers don't all set selectedSpot.
          selectedStagingTrailer?.trailer_uuid || selectedSpot?.trailerId || undefined
        );
        
        if (!shipment) {
          setAssignYardSpotError(`No shipment found with reference number "${selectedShipmentPuid}". Please verify the reference number.`);
          setValidatingShipment(false);
          setAssigningYardSpot(false);
          return;
        }

        console.log('Shipment validation successful:', shipment);
        shipmentId = shipment.id;
        setValidatingShipment(false);
      } catch (err) {
        console.error('Error validating shipment:', err);
        setAssignYardSpotError('Failed to validate shipment reference number. Please try again.');
        setValidatingShipment(false);
        setAssigningYardSpot(false);
        return;
      }
    }
    
    try {
      // Get the trailer UUID - prefer trailer_uuid if available, otherwise use puid
      const trailerUuid = selectedStagingTrailer.trailer_uuid || selectedStagingTrailer.puid;
      
      if (!trailerUuid) {
        throw new Error('No trailer UUID available');
      }

      // Determine source type and movement type
      const isFromYardSpot = selectedStagingTrailer.puid.startsWith('spot-') || 
                            yardSpots.some(spot => spot.id === selectedStagingTrailer.puid || 
                                                  spot.shipmentNumber === selectedStagingTrailer.puid ||
                                                  spot.trailerId === selectedStagingTrailer.trailer_uuid);
      
      // Improved facility zone detection: check if trailer exists in zone trailers or has zone-trailer puid
      const isFromFacilityZone = selectedStagingTrailer.puid.startsWith('zone-trailer-') ||
                                 zoneTrailers.some(zt => zt.trailer_uuid === selectedStagingTrailer.trailer_uuid ||
                                                        zt.trailer_number === selectedStagingTrailer.trailer_number);
      
      let movementType: string;
      let variables: any = {
        trailer_uuid: trailerUuid,
        facility_id: selectedFacility?.id?.toString() || "1",
        driver_id: selectedStagingTrailer.driver_id || undefined,
        shipment_id: shipmentId || undefined,
        force: false
      };

      if (selectedDestinationType === 'yard_spot') {
        // Moving to yard spot - use UUID for exact match
        const destinationSpot = moveDestinations.yardSpots.find(spot => spot.id === selectedYardSpotUuid);
        
        if (!destinationSpot) {
          throw new Error(`Yard spot with ID ${selectedYardSpotUuid} not found`);
        }

        variables.facility_zone_uuid = destinationSpot.zoneId;
        variables.yard_spot_uuid = destinationSpot.id;

        if (isFromYardSpot) {
          movementType = 'yard_to_yard';
          // Find source yard spot for yard-to-yard movement
          const sourceSpot = yardSpots.find(spot => 
            spot.id === selectedStagingTrailer.puid || 
            spot.shipmentNumber === selectedStagingTrailer.puid ||
            spot.trailerId === selectedStagingTrailer.trailer_uuid
          );
          if (sourceSpot) {
            variables.source_facility_zone_uuid = sourceSpot.zoneId;
          }
        } else if (isFromFacilityZone) {
          movementType = 'facility_zone_to_yard_spot';
          // Find source facility zone
          const sourceZoneTrailer = zoneTrailers.find(zt => 
            zt.trailer_uuid === trailerUuid || zt.puid === selectedStagingTrailer.puid
          );
          if (sourceZoneTrailer) {
            variables.source_facility_zone_uuid = sourceZoneTrailer.zone_id;
          }
        } else {
          // Default to yard_to_yard for compatibility
          movementType = 'yard_to_yard';
        }
      } else {
        // Moving to facility zone
        movementType = isFromYardSpot ? 'yard_spot_to_facility_zone' : 
                      isFromFacilityZone ? 'facility_zone_to_facility_zone' : 
                      'yard_spot_to_facility_zone'; // Default fallback

        // Always set destination_facility_zone_uuid for facility zone destinations
        variables.destination_facility_zone_uuid = selectedFacilityZone;

        if (isFromYardSpot) {
          // Find source yard spot
          const sourceSpot = yardSpots.find(spot => 
            spot.id === selectedStagingTrailer.puid || 
            spot.shipmentNumber === selectedStagingTrailer.puid ||
            spot.trailerId === selectedStagingTrailer.trailer_uuid
          );
          if (sourceSpot) {
            variables.yard_spot_uuid = sourceSpot.id;
            // For yard_spot_to_facility_zone, also set the source facility zone (the zone containing the yard spot)
            variables.facility_zone_uuid = sourceSpot.zoneId;
          }
        } else if (isFromFacilityZone) {
          // Find source facility zone for facility_zone_to_facility_zone movement
          const sourceZoneTrailer = zoneTrailers.find(zt => 
            zt.trailer_uuid === trailerUuid || zt.puid === selectedStagingTrailer.puid
          );
          if (sourceZoneTrailer) {
            variables.source_facility_zone_uuid = sourceZoneTrailer.zone_id;
          }
          // Ensure facility_zone_uuid is not conflicting with destination_facility_zone_uuid
          variables.facility_zone_uuid = null;
        }
      }

      variables.movement_type = movementType;
      
      // Add auto_complete if checked
      if (autoCompleteMoveToYardSpot) {
        variables.auto_complete = true;
      }

      console.log('Creating movement task to yard spot with variables:', variables);

      // Create the movement task using API service
      const createdTask = await createMovementTask(
        withCampusFacilities(
          { ...variables, yard_check: currentYardCheck() },
          variables.facility_id,
          moveDestinations.facilityOf(
            selectedDestinationType === 'yard_spot' ? 'yard_spot' : 'facility_zone',
            selectedDestinationType === 'yard_spot' ? selectedYardSpotUuid : selectedFacilityZone,
          ),
        ),
      );
      
      if (!createdTask?.uuid) {
        throw new Error('Failed to create movement task to yard spot');
      }

      console.log('Movement task created successfully:', createdTask);
      
      // Handle locking based on destination type
      if (selectedDestinationType === 'yard_spot') {
        const destinationSpot = yardSpots.find(spot => spot.id === selectedYardSpotUuid);
        if (destinationSpot) {
          // Lock the destination spot to show it's in a pending state
          setLockedSpots(prev => new Set(prev).add(destinationSpot.id));
        }
      }
      
      // Handle source cleanup based on movement type
      if (isFromYardSpot) {
        // If moving from yard spot, clear the source spot
        const sourceSpot = yardSpots.find(spot => 
          spot.id === selectedStagingTrailer.puid || 
          spot.shipmentNumber === selectedStagingTrailer.puid ||
          spot.trailerId === selectedStagingTrailer.trailer_uuid
        );
        if (sourceSpot) {
          updateSpotToAvailable(sourceSpot.id);
        }
      } else if (isFromFacilityZone) {
        // Remove the trailer from zone trailers since it's now moving
        setZoneTrailers(prev => 
          prev.filter(trailer => trailer.puid !== selectedStagingTrailer.puid)
        );
      } else {
        // Remove from staging trailers (fallback case)
        setStagingTrailers(prev => 
          prev.filter(trailer => trailer.puid !== selectedStagingTrailer.puid)
        );
      }
      
      // Store the task data and show success modal
      setCreatedTask(createdTask);
      setShowSuccessModal(true);
      
      // Close the modal
      handleCloseMoveToYardSpotModal();
      
      // Refresh data silently
      await refreshData(filterZone || undefined, true);
    } catch (err) {
      console.error('Error creating movement task to yard spot:', err);
      setAssignYardSpotError('Failed to move trailer to yard spot. Please try again.');
    } finally {
      setAssigningYardSpot(false);
    }
  };

  // Extract zone trailers from the existing yard data
  const extractZoneTrailers = useCallback(() => {
    if (!selectedFacility || zones.length === 0 || !facilityZones) return;
    
    setLoadingZoneTrailers(true);
    
    try {
      // Extract trailers from the facility_zones data that comes from the existing yard query
      const extractedTrailers: ZoneTrailer[] = [];
      
      facilityZones.forEach((facilityZone: any) => {
        // Process trailers if they exist, but we'll still show the zone even if empty
        if (facilityZone.trailers && facilityZone.trailers.length > 0) {
          facilityZone.trailers.forEach((trailer: any) => {
            const driver = trailer.active_lineage?.trailer_state?.driver;
            const driverName = driver ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim() : '';
            
            extractedTrailers.push({
              puid: '', // Not available in GraphQL response
              driver_id: driver?.id || '', // Extract driver ID from trailer state
              driver_name: driverName, // Construct driver name from first and last name
              driver_phone_number: '', // Not available in GraphQL response
              truck_number: '', // Not available in GraphQL response
              trailer_number: trailer.trailerNumber || '',
              direction: trailer.active_lineage?.trailer_state?.shipment?.direction || '',
              carrier_name: trailer.carrier?.name || '',
              zone_id: facilityZone.facility_zone_uuid,
              current_status: trailer.status?.name || '', // Now available from GraphQL
              trailer_uuid: trailer.uuid || '',
              shipment_number: trailer.active_lineage?.trailer_state?.shipment?.referenceNumber || '', // Add shipment number if available
              shipment_id: trailer.active_lineage?.trailer_state?.shipment?.id || null, // Add shipment ID to preserve relationship
              data: trailer.data || null, // Include trailer data for OOS access
              active_lineage: trailer.active_lineage || undefined // Include active_lineage with notes
            });
          });
        }
      });

      setZoneTrailers(extractedTrailers);
      
      // Extract staging trailers from zone trailers (only if staging zone actually exists)
      const stagingZone = zones.find(zone => zone.zone_type === "staging");
      if (stagingZone) {
        const stagingTrailersFromZones = extractedTrailers
          .filter(trailer => trailer.zone_id === stagingZone.id)
          .map(trailer => ({
            puid: trailer.puid,
            driver_id: trailer.driver_id,
            driver_name: trailer.driver_name,
            driver_phone_number: trailer.driver_phone_number || '',
            truck_number: trailer.truck_number || '',
            trailer_number: trailer.trailer_number,
            direction: trailer.direction,
            carrier_name: trailer.carrier_name || '',
            trailer_uuid: trailer.trailer_uuid,
            current_status: trailer.current_status
          }));
        
        setStagingTrailers(stagingTrailersFromZones);
      } else {
        // Clear staging trailers if no staging zone exists
        setStagingTrailers([]);
      }
      
    } catch (err) {
      console.error('Error extracting zone trailers:', err);
    } finally {
      setLoadingZoneTrailers(false);
      setLoadingStagingTrailers(false);
    }
  }, [selectedFacility, zones, facilityZones]);

  // Extract staging trailers from yard data or extract zone trailers
  useEffect(() => {
    if (selectedFacility && zones.length > 0) {
      setLoadingStagingTrailers(true);
      // Extract zone-based trailers from existing data
      extractZoneTrailers();
    }
  }, [selectedFacility, zones, extractZoneTrailers]);
  
  // Only call refresh on manual refresh
  const handleRefreshData = useCallback(() => {
    refreshData(filterZone || undefined);
    extractZoneTrailers();
  }, [refreshData, extractZoneTrailers, filterZone]);

  // Process warehouse dock doors from background-loaded data
  useEffect(() => {
    if (warehouseData?.warehouseZones) {
      const dockDoors: Array<{door: string, dockUuid: string, zoneUuid: string, zoneName: string, hasMoveFromTask?: boolean}> = [];
      
      warehouseData.warehouseZones.forEach((zone: any) => {
        if (zone.docks) {
          zone.docks.forEach((dock: any) => {
            if (isDockUnavailableForMovement(dock.configuration)) {
              return;
            }

            // Only include docks that are available (no trailer assigned)
            const isDockOccupied = dock.trailer_uuid || dock.trailer_name;
            if (!isDockOccupied) {
              dockDoors.push({
                door: dock.dockName,
                dockUuid: dock.dockUuid,
                zoneUuid: zone.zoneUuid,
                zoneName: zone.zoneName,
                hasMoveFromTask: false
              });
            }
          });
        }
      });
      
      setActiveDockDoors(dockDoors);
      setLoadingDockDoors(false);
    } else {
      setActiveDockDoors([]);
      setLoadingDockDoors(false);
    }
  }, [warehouseData]);

  // Note: Zone filter changes are handled by the useYardData hook's internal useEffect
  // No need to refetch here as it would cause duplicate calls

  // Update the handleMoveToDock function to process dock doors
  const handleMoveToDock = (spot: LocalYardSpot) => {
    // Set the selected spot for the modal
    setSelectedSpot(spot);
    
    // Check if trailer is OOS before allowing move to dock
    if (isTrailerOOS(spot)) {
      setMoveToDockError('Cannot move trailer to dock: Trailer is out of service (OOS)');
      return;
    }
    
    setSelectedDockDoor('');
    setDockFilter('');
    setSelectedShipmentPuid(spot.shipmentNumber || '');
    setMoveToDockError(null);
    setShowMoveToDockModal(true);
  };

  // Handle move to dock directly from tile
  const handleMoveToDockFromTile = (spot: LocalYardSpot) => {
    // Check if trailer is OOS before allowing move to dock
    if (isTrailerOOS(spot)) {
      setMoveToDockError('Cannot move trailer to dock: Trailer is out of service (OOS)');
      return;
    }
    
    setSelectedSpot(spot);
    setSelectedDockDoor('');
    setDockFilter('');
    setSelectedShipmentPuid(spot.shipmentNumber || '');
    setMoveToDockError(null);
    setShowMoveToDockModal(true);
  };

  // Handle move spot to spot directly from tile
  const handleMoveSpotToSpot = (spot: LocalYardSpot) => {
    // Convert spot to StagingTrailer format for compatibility with existing modal
    const stagingTrailer: StagingTrailer = {
      puid: spot.shipmentNumber || spot.id,
      driver_id: spot.driverId || '',
      driver_name: spot.driverName || '',
      driver_phone_number: '',
      truck_number: '',
      trailer_number: spot.trailerNumber || '',
      direction: spot.direction || '',
      carrier_name: spot.carrier_name || '',
      trailer_uuid: spot.trailerId || spot.id,
      current_status: spot.status === 'loaded' ? 'LOADED' : undefined
    };
    
    setSelectedStagingTrailer(stagingTrailer);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setAssignYardSpotError(null);
    // Pre-populate shipment reference from the spot's shipment number
    setSelectedShipmentPuid(spot.shipmentNumber || '');
    setShowMoveToYardSpotModal(true);
  };

  // Handle move trailer to adjacent spot (left/right arrows)
  const handleMoveToAdjacentSpot = async (fromSpot: LocalYardSpot, toSpot: LocalYardSpot) => {
    try {
      // Set moving state to disable all arrows
      setIsMovingTrailer(true);
      
      // Lock both spots immediately
      setLockedSpots(prev => {
        const newSet = new Set(prev);
        newSet.add(fromSpot.id);
        newSet.add(toSpot.id);
        return newSet;
      });

      // Create auto-complete movement task
      await createMovementTask({
        trailer_uuid: fromSpot.trailerId || '',
        movement_type: 'yard_to_yard',
        facility_id: selectedFacility?.id?.toString() || "1",
        yard_spot_uuid: toSpot.id,
        facility_zone_uuid: toSpot.zoneId,
        source_facility_zone_uuid: fromSpot.zoneId,
        shipment_id: fromSpot.bol_id || null,
        driver_id: fromSpot.driverId || undefined,
        auto_complete: true,
        force: false,
        yard_check: currentYardCheck()
      });

      // Refresh data to show the move (silent refresh - no loading indicator)
      await refreshData(filterZone || undefined, true);
      
      // Unlock spots after successful move
      setLockedSpots(prev => {
        const newSet = new Set(prev);
        newSet.delete(fromSpot.id);
        newSet.delete(toSpot.id);
        return newSet;
      });
      
      // Re-enable arrows
      setIsMovingTrailer(false);
    } catch (error: any) {
      console.error('Error moving trailer to adjacent spot:', error);
      alert(`Failed to move trailer: ${error.message}`);
      
      // Unlock spots on error
      setLockedSpots(prev => {
        const newSet = new Set(prev);
        newSet.delete(fromSpot.id);
        newSet.delete(toSpot.id);
        return newSet;
      });
      
      // Re-enable arrows on error
      setIsMovingTrailer(false);
    }
  };

  // Handle quick unassign trailer from spot
  const handleQuickUnassignTrailer = async (spot: LocalYardSpot, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent tile click
    }
    
    if (!spot.trailerNumber) return;
    
    const confirmed = window.confirm(`Unassign trailer ${spot.trailerNumber}? It will be moved to the staging zone.`);
    if (!confirmed) return;
    
    setIsMovingTrailer(true);
    
    try {
      // Find the staging zone from all zones (full unfiltered data)
      const stagingZone = allZones.find((zone: Zone) => zone.zone_type === "staging");
      
      if (!stagingZone) {
        throw new Error('Staging zone not found. Please reload the page.');
      }
      
      // Create movement task to move trailer to staging
      await createMovementTask({
        trailer_uuid: spot.trailerId || '',
        movement_type: 'yard_spot_to_facility_zone',
        facility_id: selectedFacility?.id?.toString() || "1",
        yard_spot_uuid: spot.id,
        facility_zone_uuid: spot.zoneId,
        destination_facility_zone_uuid: stagingZone.id,
        shipment_id: spot.bol_id || undefined,
        driver_id: spot.driverId || undefined,
        auto_complete: true,
        force: false,
        yard_check: currentYardCheck()
      });
      
      // Refresh data
      await refreshData(filterZone || undefined, true);
      
      setIsMovingTrailer(false);
    } catch (error: any) {
      console.error('Error unassigning trailer:', error);
      alert(`Failed to unassign trailer: ${error.message}`);
      setIsMovingTrailer(false);
    }
  };

  // Handle swapping two trailers between adjacent spots
  const handleSwapTrailers = async (spot1: LocalYardSpot, spot2: LocalYardSpot) => {
    try {
      // Set moving state to disable all arrows
      setIsMovingTrailer(true);
      
      // Lock both spots immediately
      setLockedSpots(prev => {
        const newSet = new Set(prev);
        newSet.add(spot1.id);
        newSet.add(spot2.id);
        return newSet;
      });

      // Create both movement tasks in a single GraphQL request
      await swapTrailersBetweenSpots(
        {
          trailer_uuid: spot1.trailerId || '',
          movement_type: 'yard_to_yard',
          facility_id: selectedFacility?.id?.toString() || "1",
          yard_spot_uuid: spot2.id,
          facility_zone_uuid: spot2.zoneId,
          source_facility_zone_uuid: spot1.zoneId,
          shipment_id: spot1.bol_id || null,
          driver_id: spot1.driverId || undefined,
          auto_complete: true,
          force: false
        },
        {
          trailer_uuid: spot2.trailerId || '',
          movement_type: 'yard_to_yard',
          facility_id: selectedFacility?.id?.toString() || "1",
          yard_spot_uuid: spot1.id,
          facility_zone_uuid: spot1.zoneId,
          source_facility_zone_uuid: spot2.zoneId,
          shipment_id: spot2.bol_id || null,
          driver_id: spot2.driverId || undefined,
          auto_complete: true,
          force: false
        },
        currentYardCheck()
      );

      // Refresh data to show the swap (silent refresh - no loading indicator)
      await refreshData(filterZone || undefined, true);
      
      // Unlock spots after successful swap
      setLockedSpots(prev => {
        const newSet = new Set(prev);
        newSet.delete(spot1.id);
        newSet.delete(spot2.id);
        return newSet;
      });
      
      // Re-enable arrows
      setIsMovingTrailer(false);
    } catch (error: any) {
      console.error('Error swapping trailers:', error);
      alert(`Failed to swap trailers: ${error.message}`);
      
      // Unlock spots on error
      setLockedSpots(prev => {
        const newSet = new Set(prev);
        newSet.delete(spot1.id);
        newSet.delete(spot2.id);
        return newSet;
      });
      
      // Re-enable arrows on error
      setIsMovingTrailer(false);
    }
  };

  // Handle move zone trailer to yard spot
  const handleMoveZoneTrailerToYardSpot = (trailer: ZoneTrailer) => {
    // Convert ZoneTrailer to StagingTrailer format for compatibility with existing modal
    const stagingTrailer: StagingTrailer = {
      puid: trailer.puid,
      driver_id: trailer.driver_id,
      driver_name: trailer.driver_name,
      driver_phone_number: trailer.driver_phone_number || '',
      truck_number: trailer.truck_number || '',
      trailer_number: trailer.trailer_number,
      direction: trailer.direction,
      carrier_name: trailer.carrier_name || '',
      trailer_uuid: trailer.trailer_uuid, // Pass the trailer UUID
      current_status: trailer.current_status // Pass the current status for loaded detection
    };
    
    setSelectedStagingTrailer(stagingTrailer);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setAssignYardSpotError(null);
    // Pre-populate shipment reference from the trailer's shipment reference number
    setSelectedShipmentPuid(trailer.shipment_number || trailer.puid || '');
    setShowMoveToYardSpotModal(true);
  };

  // Handle move zone trailer directly to dock
  const handleMoveZoneTrailerToDock = (trailer: ZoneTrailer) => {
    // Check if trailer is OOS before allowing move to dock
    if (isTrailerOOS(trailer)) {
      setMoveToDockError('Cannot move trailer to dock: Trailer is out of service (OOS)');
      return;
    }
    
    // Convert ZoneTrailer to LocalYardSpot format for compatibility with existing modal
    const spotData: LocalYardSpot = {
      id: `zone-trailer-${trailer.trailer_uuid || trailer.puid}`,
      number: 0, // Use 0 as default since zone trailers don't have spot numbers
      displayNumber: `Zone-${trailer.trailer_number}`,
      status: 'in-use' as Status,
      zoneId: trailer.zone_id,
      trailerNumber: trailer.trailer_number,
      driverName: trailer.driver_name,
      shipmentNumber: trailer.shipment_number || trailer.puid,
      carrier_name: trailer.carrier_name || null,
      // Add other required fields with defaults
      order_number: 0,
      driverId: trailer.driver_id,
      trailerId: trailer.trailer_uuid || trailer.puid, // Use trailer UUID if available, fallback to puid
      data: trailer.data, // Include data field for OOS access
      scheduledTimes: [],
      current_status: trailer.current_status
    };
    
    setSelectedSpot(spotData);
    setSelectedDockDoor('');
    setDockFilter('');
    setSelectedShipmentPuid(trailer.shipment_number || trailer.puid || '');
    setMoveToDockError(null);
    setShowMoveToDockModal(true);
    };

  // Handle closing zone trailer details modal
  const handleCloseZoneTrailerDetailsModal = () => {
    setShowZoneTrailerDetailsModal(false);
    setSelectedZoneTrailer(null);
  };

  // Get all existing trailers in the yard using trailers_in_facility query
  // Returns trailers from yard spots and zones, but NOT warehouse/dock doors
  const getAllExistingTrailers = useCallback(async () => {
    if (!selectedFacility) return [];
    
    try {
      const { getTrailersInFacility } = await import('./api');
      const trailersData = await getTrailersInFacility(selectedFacility.id.toString());
      
      const trailers = trailersData.map((trailer: any) => {
        let location = 'Unknown';
        let isAtDock = false;
        
        if (trailer.current_location) {
          const locationType = trailer.current_location.__typename;
          
          if (locationType === 'YardSpotObject') {
            location = `Yard Spot ${trailer.current_location.name}`;
          } else if (locationType === 'FacilityZoneObject') {
            location = `Zone: ${trailer.current_location.name}`;
          } else if (locationType === 'DockObject') {
            location = `Dock: ${trailer.current_location.name}`;
            isAtDock = true;
          }
        }
        
        return {
          scac: trailer.carrier?.name || '',
          trailerNumber: trailer.trailer_number?.trim() || '',
          trailerUuid: trailer.uuid,
          location,
          isAtDock,
          shipmentId: trailer.active_lineage?.trailer_state?.shipment?.id || undefined,
          shipmentNumber: trailer.active_lineage?.trailer_state?.shipment?.reference_number || undefined,
          trailerStatus: trailer.status?.name || undefined
        };
      });
      
      // Filter out trailers at docks (warehouse) and trailers without numbers
      // This ensures we only get trailers from yard spots and zones
      return trailers.filter((t: any) => t.trailerNumber && !t.isAtDock);
    } catch (error) {
      console.error('Error fetching trailers:', error);
      return [];
    }
  }, [selectedFacility]);
  
  // Load existing trailers when the unified details modal opens
  useEffect(() => {
    if (showUnifiedDetailsModal && selectedFacility) {
      getAllExistingTrailers().then(trailers => {
        setExistingTrailersList(trailers);
      });
    }
  }, [showUnifiedDetailsModal, selectedFacility, getAllExistingTrailers]);

  // Load existing trailers when the quick add modal opens
  useEffect(() => {
    if (showQuickAddModal && selectedFacility) {
      getAllExistingTrailers().then(trailers => {
        setExistingTrailersList(trailers);
      });
    }
  }, [showQuickAddModal, selectedFacility, getAllExistingTrailers]);

  // Handle adding trailer to empty spot
  const handleAddTrailerToSpot = async (forceCreate: boolean = false) => {
    if (!selectedUnifiedItem || selectedUnifiedItem.type !== 'yard_spot') return;
    
    const spot = selectedUnifiedItem.originalData as LocalYardSpot;
    
    if (!addTrailerScac.trim() || !addTrailerNumber.trim()) {
      setAddTrailerError('Please enter both SCAC and trailer number (e.g., "ABCD 12345")');
      return;
    }
    
    setIsAddingTrailer(true);
    setAddTrailerError(null);
    
    try {
      // Check if trailer already exists in the yard spots or zones (but NOT warehouse)
      // getAllExistingTrailers already filters out dock trailers (isAtDock)
      const existingTrailers = await getAllExistingTrailers();
      const existingTrailer = existingTrailers.find((t: any) => 
        t.scac.toLowerCase() === addTrailerScac.trim().toLowerCase() &&
        t.trailerNumber.toLowerCase() === addTrailerNumber.trim().toLowerCase()
      );
      
      if (existingTrailer) {
        // Trailer exists in yard or zone - create movement task to move it to this spot
        const movementType = 'yard_to_yard'; // Simplified for now
        
        // Get the shipment number from the trailer (was captured when selecting from autocomplete)
        const shipmentReferenceNumber = addTrailerShipmentNumber || existingTrailer.shipmentNumber;
        let shipmentId = null;
        
        // If we have a shipment reference, look up the shipment ID
        if (shipmentReferenceNumber && shipmentReferenceNumber.trim()) {
          const shipment = await getShipmentByReferenceNumber(
            shipmentReferenceNumber,
            selectedFacility?.id?.toString() || "1"
          );
          
          if (shipment?.id) {
            shipmentId = shipment.id;
          }
        }
        
        await createMovementTask({
          trailer_uuid: existingTrailer.trailerUuid,
          movement_type: movementType,
          facility_id: selectedFacility?.id?.toString() || "1",
          yard_spot_uuid: spot.id,
          facility_zone_uuid: spot.zoneId,
          force: false,
          auto_complete: true,
          shipment_id: shipmentId || null,
          yard_check: currentYardCheck()
        });
        
        alert(`Trailer ${addTrailerNumber} moved to ${spot.displayNumber}`);
        
      } else {
        // Trailer doesn't exist in yard/zones - create new trailer at this spot
        const trailerData = await addTrailer({
          scac: addTrailerScac.trim(),
          trailerNumber: addTrailerNumber.trim(),
          facilityId: selectedFacility?.id?.toString() || "1",
          status: addTrailerStatus,
          yardSpotUuid: spot.id,
          facilityZoneUuid: spot.zoneId,
          notes: null,
          force_create_trailer_if_active: forceCreate
        });
        
        if (!trailerData?.success) {
          throw new Error(trailerData?.errors?.join(', ') || 'Failed to add trailer');
        }
        
        alert(`Trailer ${addTrailerNumber} added to ${spot.displayNumber}`);
      }
      
      // Reset form and refresh data
      setAddTrailerScac('');
      setAddTrailerNumber('');
      setAddTrailerStatus('EMPTY');
      setAddTrailerShipmentNumber('');
      handleCloseUnifiedModal();
      await refreshData(filterZone || undefined, true);
      
    } catch (error: any) {
      console.error('Error adding trailer to spot:', error);
      const isTimeout = error.name === 'AbortError' || error.message?.includes('timeout');

      if (isTimeout) {
        // Connection died but server may have succeeded — do a lightweight zone refresh
        await refreshZone(spot.zoneId);
        handleCloseUnifiedModal();
      } else {
        const errorMessage = error.message || 'Failed to add trailer';
        setAddTrailerError(errorMessage);

        // Check if error contains "is already active"
        if (errorMessage.toLowerCase().includes('is already active')) {
          setShowCreateAnywayButton(true);
        }
      }
    } finally {
      setIsAddingTrailer(false);
    }
  };

  // Handle create anyway (force create despite "is already active" error)
  const handleCreateAnyway = () => {
    setShowCreateAnywayButton(false);
    handleAddTrailerToSpot(true);
  };

  // Handle unassigning trailer (send to staging zone)
  const handleUnassignTrailer = async () => {
    if (!selectedUnifiedItem || !selectedUnifiedItem.trailerNumber) return;
    
    // Confirm action
    const confirmed = window.confirm(`Are you sure you want to unassign trailer ${selectedUnifiedItem.trailerNumber}? It will be moved to the staging zone.`);
    if (!confirmed) return;
    
    setIsUnassigningTrailer(true);
    
    try {
      // Get auth token
      const authToken = getAuthToken();
      
      // Find the staging zone from the existing zones data using zone_type property
      const stagingZone = zones.find((zone: Zone) => zone.zone_type === "staging");
      
      if (!stagingZone) {
        throw new Error('Staging zone not found. Please ensure a facility zone with zone_type "staging" exists.');
      }
      
      // Determine movement type and variables based on source
      let movementType: string;
      let trailerUuid: string;
      let shipmentId: string | null = null;
      let driverId: string | null = null;
      const variables: any = {
        facility_id: selectedFacility?.id?.toString() || "1",
        destination_facility_zone_uuid: stagingZone.id,
        force: false,
        auto_complete: true  // Auto-complete the task
      };
      
      if (selectedUnifiedItem.type === 'yard_spot') {
        // Moving from yard spot to staging
        movementType = 'yard_spot_to_facility_zone';
        const spot = selectedUnifiedItem.originalData as LocalYardSpot;
        trailerUuid = spot.trailerId || '';
        shipmentId = spot.bol_id || null; // Get shipment_id from yard spot
        driverId = spot.driverId || null; // Get driver_id from yard spot
        variables.yard_spot_uuid = spot.id;
        variables.facility_zone_uuid = spot.zoneId; // Source zone
      } else if (selectedUnifiedItem.type === 'zone_trailer') {
        // Moving from facility zone to staging
        movementType = 'facility_zone_to_facility_zone';
        const zoneTrailer = selectedUnifiedItem.originalData as ZoneTrailer;
        trailerUuid = zoneTrailer.trailer_uuid || '';
        // Get shipment_id from zone trailer if it exists
        shipmentId = (zoneTrailer as any).shipment_id || null;
        // Get driver_id from zone trailer if it exists
        driverId = zoneTrailer.driver_id || null;
        variables.source_facility_zone_uuid = zoneTrailer.zone_id;
      } else {
        throw new Error('Unknown source type for unassigning trailer');
      }
      
      variables.trailer_uuid = trailerUuid;
      variables.movement_type = movementType;
      
      // Always include shipment_id if it exists to preserve shipment relationship
      if (shipmentId) {
        variables.shipment_id = shipmentId;
      }
      
      // Always include driver_id if it exists to preserve driver assignment
      if (driverId) {
        variables.driver_id = driverId;
      }
      
      // Create the movement task
      const createdTask = await createMovementTask({ ...variables, yard_check: currentYardCheck() });
      
      console.log('Trailer unassigned successfully:', createdTask);
      
      // Update local state based on source type
      if (selectedUnifiedItem.type === 'yard_spot') {
        // Clear the yard spot
        const spot = selectedUnifiedItem.originalData as LocalYardSpot;
        updateSpotToAvailable(spot.id);
      } else if (selectedUnifiedItem.type === 'zone_trailer') {
        // Remove from zone trailers (it will reappear in staging zone after backend processes it)
        const zoneTrailer = selectedUnifiedItem.originalData as ZoneTrailer;
        setZoneTrailers(prev => 
          prev.filter(trailer => trailer.puid !== zoneTrailer.puid)
        );
      }
      
      // Close the modal
      handleCloseUnifiedModal();
      
      // Refresh data silently
      await refreshData(filterZone || undefined, true);
      
      setIsUnassigningTrailer(false);
      
    } catch (error: any) {
      console.error('Error unassigning trailer:', error);
      alert(`Failed to unassign trailer: ${error.message}`);
      setIsUnassigningTrailer(false);
    }
  };

  // Function to save trailer lineage notes
  const handleSaveTrailerNotes = async () => {
    if (!selectedZoneTrailer || !selectedZoneTrailer.trailer_uuid) return;
    
    setSavingTrailerNotes(true);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Update trailer notes
      const result = await updateTrailer({
        uuid: selectedZoneTrailer.trailer_uuid,
        input: {
          notes: trailerNotesValue
        }
      });

      // Update the selected trailer with the new notes immediately for UI feedback
      if (selectedZoneTrailer.active_lineage) {
        selectedZoneTrailer.active_lineage.notes = trailerNotesValue;
      } else {
        selectedZoneTrailer.active_lineage = {
          uuid: result.active_lineage?.uuid || '',
          notes: trailerNotesValue
        };
      }
      
      // Exit edit mode
      setIsEditingTrailerNotes(false);
      
             // Refresh data to ensure consistency
       await refreshData(filterZone || undefined);
       
     } catch (error: any) {
       console.error('Failed to save trailer notes:', error);
       // Note: Error handling would be shown in UI via error state from the hook
     } finally {
       setSavingTrailerNotes(false);
     }
  };

  // Function to save spot trailer lineage notes
  const handleSaveSpotTrailerNotes = async () => {
    // Get trailer UUID from either selectedSpot or selectedUnifiedItem (for zone trailers)
    const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;
    if (!trailerUuid) return;
    
    setSavingSpotTrailerNotes(true);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Update trailer notes
      const result = await updateTrailer({
        uuid: trailerUuid,
        input: {
          notes: spotTrailerNotesValue
        }
      });

      // Update the UI state with the new notes immediately for UI feedback
      if (selectedSpot?.active_lineage) {
        selectedSpot.active_lineage.notes = spotTrailerNotesValue;
      } else if (selectedSpot) {
        selectedSpot.active_lineage = {
          uuid: result.active_lineage?.uuid || '',
          notes: spotTrailerNotesValue
        };
      }
      
      // Also update unified item if it exists
      if (selectedUnifiedItem?.active_lineage) {
        selectedUnifiedItem.active_lineage.notes = spotTrailerNotesValue;
      } else if (selectedUnifiedItem) {
        selectedUnifiedItem.active_lineage = {
          uuid: result.active_lineage?.uuid || '',
          notes: spotTrailerNotesValue
        };
      }
      
      // Exit edit mode and clear the notes value
      setIsEditingSpotTrailerNotes(false);
      setSpotTrailerNotesValue('');
      
      // Refresh data to ensure consistency
      await refreshData(filterZone || undefined);
      
    } catch (error: any) {
      console.error('Failed to save spot trailer notes:', error);
      // Note: Error handling would be shown in UI via error state from the hook
    } finally {
      setSavingSpotTrailerNotes(false);
    }
  };

  // Validate shipment reference and get shipment ID (local helper)
  const validateShipmentReferenceLocal = async (referenceNumber: string): Promise<string | null> => {
    if (!referenceNumber.trim()) return null;

    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Get trailer UUID from either selectedSpot or selectedUnifiedItem
      const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;

      const shipment = await validateShipmentReference(
        referenceNumber.trim(),
        selectedFacility?.id?.toString() || "1",
        trailerUuid
      );
      
      if (!shipment?.id) {
        throw new Error(`No unassigned shipment found with reference number: ${referenceNumber.trim()}`);
      }

      return shipment.id;
    } catch (err: any) {
      console.error('Error validating shipment reference:', err);
      throw err;
    }
  };

  // Handle update trailer shipment
  const handleUpdateTrailerShipment = async () => {
    // Get trailer UUID from either selectedSpot or selectedUnifiedItem
    const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;
    if (!trailerUuid) return;
    
    try {
      setIsUpdatingShipment(true);
      
      let shipmentId = null;
      
      // Validate shipment reference if provided
      if (newShipmentReference.trim()) {
        try {
          shipmentId = await validateShipmentReferenceLocal(newShipmentReference);
          setShipmentReferenceError(''); // Clear any previous error
        } catch (validationError: any) {
          setShipmentReferenceError(validationError.message);
          setIsUpdatingShipment(false);
          return;
        }
      } else {
        setShipmentReferenceError(''); // Clear error if no reference number
      }
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      await updateTrailer({
        uuid: trailerUuid,
        input: {
          shipmentId: shipmentId
        }
      });

      // Refresh data to show updated shipment
      await refreshData(filterZone || undefined);
      
      // Clear the input and close the modal
      setNewShipmentReference('');
      setShowUnifiedDetailsModal(false);
      setSelectedUnifiedItem(null);
      setSelectedSpot(null);
      
    } catch (error: any) {
      console.error('Failed to update shipment:', error);
      setShipmentReferenceError(error.message || 'Failed to update shipment. Please try again.');
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  // Helper function to determine if remove shipment button should be enabled
  const isRemoveShipmentButtonEnabled = () => {
    const shipmentNumber = selectedSpot?.shipmentNumber || selectedUnifiedItem?.shipmentNumber;
    if (!shipmentNumber) return false;
    
    // Enable button for yard spots - allow removal from yard
    return true;
  };

  // Function to remove shipment relationship
  const handleRemoveShipmentRelationship = async () => {
    // Get trailer UUID from either selectedSpot or selectedUnifiedItem
    const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;
    if (!trailerUuid) return;
    
    try {
      setIsRemovingShipment(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      await updateTrailer({
        uuid: trailerUuid,
        input: {
          shipmentId: null
        }
      });

      // Close confirmation modal and refresh data
      setShowRemoveShipmentConfirm(false);
      await refreshData(filterZone || undefined);
      
    } catch (error: any) {
      console.error('Failed to remove shipment relationship:', error);
      alert('Failed to remove shipment relationship. Please try again.');
    } finally {
      setIsRemovingShipment(false);
    }
  };

  // Add handler for closing the move to dock modal
  const handleCloseMoveToDockModal = () => {
    setShowMoveToDockModal(false);
    setSelectedDockDoor('');
    setDockFilter('');
    setSelectedShipmentPuid('');
    setAutoCompleteMoveToYardSpot(false);
    setMoveToDockError(null);
  };

    // Update handler for moving trailer to dock using create_movement_task
  const handleMoveTrailerToDock = async () => {
    if (!selectedDockDoor) {
      setMoveToDockError('Dock door is required');
      return;
    }

        if (!selectedSpot) {
      setMoveToDockError('No yard spot selected');
      return;
    }

    // Shipment reference number is now required
    // if (!selectedShipmentPuid || !selectedShipmentPuid.trim()) {
    //   setMoveToDockError('Shipment reference number is required');
    //   return;
    // }

    setMovingToDock(true);
    setMoveToDockError(null);
    
    let shipmentId = null;
    
    // Always validate reference number since it's required
    setValidatingShipment(true);
      
    if (selectedShipmentPuid && selectedShipmentPuid.trim()) {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Validate that the shipment reference number exists
      const shipment = await validateShipmentReference(
        selectedShipmentPuid.trim(),
        selectedFacility?.id?.toString() || "1",
        selectedSpot?.trailerId || undefined
      );

      // Check if shipment was found
      if (!shipment || !shipment.id) {
        setMoveToDockError(`No shipment found with reference number "${selectedShipmentPuid}". Please verify the reference number.`);
        setValidatingShipment(false);
        setMovingToDock(false);
        return;
      }

      console.log('Shipment validation successful:', shipment);
      shipmentId = shipment.id;
      setValidatingShipment(false);
    } catch (err) {
      console.error('Error validating shipment:', err);
      setMoveToDockError('Failed to validate reference number. Please try again.');
        setValidatingShipment(false);
        setMovingToDock(false);
        return;
      }
    }
    else{
        const confirmed = window.confirm('Are you sure you want to move the trailer to dock without a shipment reference number?');
        if (!confirmed) {
          setMovingToDock(false);
          setValidatingShipment(false);
          setMoveToDockError('Failed to validate reference number. Please try again.');
          return;
        }
    }
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Prepare to create movement task to dock

      // Find the warehouse zone for the selected dock door
      // Support both UUID (from button clicks) and door name (from manual input)
      let selectedDockInfo = moveDestinations.dockDoors.find(dockInfo => dockInfo.dockUuid === selectedDockDoor);
      
      // If not found by UUID, try to find by door name (manual input)
      if (!selectedDockInfo) {
        selectedDockInfo = moveDestinations.dockDoors.find(dockInfo => dockInfo.door === selectedDockDoor);
      }
      
      if (!selectedDockInfo) {
        throw new Error(`Dock door ${selectedDockDoor} not found in active dock doors`);
      }
      
      // Prepare variables for the mutation
      const variables: any = {
        trailer_uuid: selectedSpot.trailerId, 
        movement_type: 'yard_to_warehouse',
        facility_id: selectedFacility?.id.toString() || "1",
        dock_uuid: selectedDockInfo.dockUuid, // Use the actual dock UUID, not the dock name
        warehouse_zone_uuid: selectedDockInfo.zoneUuid,
        source_facility_zone_uuid: null,
        destination_facility_zone_uuid: null,
        driver_id: selectedSpot.driverId || undefined,
        shipment_id: shipmentId || undefined, // Use the validated shipment ID if available
        trailer_status_uuid: undefined, // Optional field
        force: false
      };

      // Add yard spot info if this is coming from a yard spot
      if (selectedSpot.id && !selectedSpot.id.startsWith('zone-trailer-')) {
        variables.facility_zone_uuid = selectedSpot.zoneId;
        variables.yard_spot_uuid = selectedSpot.id;
      }
      
      // Add auto_complete if checked
      if (autoCompleteMoveToYardSpot) {
        variables.auto_complete = true;
      }

      console.log('Creating movement task to dock with variables:', variables);

      // Execute the movement task creation
      const createdTask = await createMovementTask(
        withCampusFacilities(
          { ...variables, yard_check: currentYardCheck() },
          variables.facility_id,
          selectedDockInfo.facilityId
        ),
      );

      // Check if the movement task was created successfully
      if (!createdTask?.uuid) {
        throw new Error('Failed to create movement task to dock');
      }

      console.log('Movement task to dock created successfully:', createdTask);
      
      // Lock the spot or zone trailer to show it's in a pending state
      if (selectedSpot) {
        if (selectedSpot.id.startsWith('zone-trailer-')) {
          // It's a zone trailer
          const trailerId = selectedSpot.id.replace('zone-trailer-', '');
          setLockedZoneTrailers(prev => new Set(prev).add(trailerId));
        } else {
          // It's a yard spot
          setLockedSpots(prev => new Set(prev).add(selectedSpot.id));
        }
      }
      
      // Store the task data and show success modal
      setCreatedTask(createdTask);
      setShowSuccessModal(true);
      
      // Update local state after successful API call
      if (selectedSpot && !selectedSpot.id.startsWith('zone-trailer-')) {
        updateSpotToAvailable(selectedSpot.id);
      }
      
      // Close both modals
      handleCloseMoveToDockModal();
      if (showUnifiedDetailsModal) {
        handleCloseUnifiedModal();
      }
      
      // Refresh data silently
      await refreshData(filterZone || undefined, true);
    } catch (err) {
      console.error('Error creating movement task to dock:', err);
      setMoveToDockError('Failed to move trailer to dock. Please try again.');
    } finally {
      setMovingToDock(false);
      setValidatingShipment(false);
    }
  };

  // Function to fetch and show task details from a lock
  const handleShowTaskFromLock = async (lock: any) => {
    if (!lock.task_uuid) {
      console.error('No task UUID in lock object');
      return;
    }

    try {
      // Get all locks for the same resource to find all related tasks
      const resourceUuid = lock.resource_uuid || lock.resource?.uuid;
      const allResourceLocks = resourceUuid ? activeLocks.filter((resourceLock: any) => {
        const lockUuid = resourceLock.resource_uuid || resourceLock.resource?.uuid;
        return lockUuid === resourceUuid;
      }) : [lock];
      
      // Extract all task UUIDs from the locks
      const taskUuids = allResourceLocks
        .filter(resourceLock => resourceLock.task_uuid)
        .map(resourceLock => resourceLock.task_uuid);

      if (taskUuids.length === 0) {
        console.error('No task UUIDs found in locks');
        return;
      }

      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Fetch all tasks for the UUIDs
      const taskPromises = taskUuids.map(async (taskUuid) => {
        try {
          const task = await getTaskByUuid(taskUuid);
          return task || null;
        } catch (error) {
          console.error(`Failed to fetch task ${taskUuid}:`, error);
          return null;
        }
      });

      const tasks = (await Promise.all(taskPromises)).filter(task => task !== null);

      if (tasks.length === 0) {
        // Fallback to original single task approach
        console.warn('Failed to fetch any tasks, falling back to single task');
        const fallbackTask = {
          uuid: lock.task_uuid,
          task_status: { status: 'ACTIVE' },
          task_type: { type: lock.operation_type || 'Movement Task' },
          from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
          to_location: 'Unknown',
          trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
          created_at: lock.created_at,
          data: {
            reason: lock.reason,
            operationType: lock.operation_type
          }
        };
        setSelectedTaskForDetails(fallbackTask);
        setShowTaskDetailsModal(true);
        return;
      }

      // Prioritize MOVE_TRAILER tasks - show MOVE_TRAILER if it exists, otherwise show first task
      const moveTrailerTask = tasks.find(task => 
        task.task_type?.type === 'MOVE_TRAILER' &&
        task.task_status?.status !== 'COMPLETED' && 
        task.task_status?.status !== 'CANCELLED'
      );
      
      const selectedTask = moveTrailerTask || tasks[0];
      setSelectedTaskForDetails(selectedTask);
      setShowTaskDetailsModal(true);

    } catch (error) {
      console.error('Error fetching task details:', error);
      // Show fallback task details from lock
      const fallbackTask = {
        uuid: lock.task_uuid,
        task_status: { status: 'ACTIVE' },
        task_type: { type: lock.operation_type || 'Movement Task' },
        from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
        to_location: 'Unknown',
        trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
        created_at: lock.created_at,
        data: {
          reason: lock.reason,
          operationType: lock.operation_type
        }
      };
      setSelectedTaskForDetails(fallbackTask);
      setShowTaskDetailsModal(true);
    }
  };

  // Main component render
  return (
    <div className={window.location.search.includes('mode=android') ? 'p-5 w-full' : 'p-5 max-w-7xl mx-auto'}>
      {/* Error handling */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-5" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      
      {/* Header - Always show regardless of loading state or yard spots */}
      <div className="bg-white p-5 shadow rounded">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold">Yard Check</h1>
          
          <div className="flex flex-col items-end">
            {/* Refresh Button */}
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white flex items-center px-4 py-2 rounded shadow"
              onClick={handleRefreshData}
              disabled={apiLoading || loadingStagingTrailers || loadingZoneTrailers}
            >
              {apiLoading || loadingStagingTrailers || loadingZoneTrailers ? (
                <>
                  <CircularProgress size={16} className="mr-2" color="inherit" />
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <Refresh fontSize="small" className="mr-2" />
                  <span>Refresh Data</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Content based on loading state and data availability */}
      {apiLoading ? (
        <div className="flex justify-center items-center h-64">
          <CircularProgress />
        </div>
      ) : yardSpots.length === 0 ? (
        <div className="flex flex-col gap-5">
          {/* Show facility selector even when no spots */}
          <div className="flex flex-wrap gap-3 items-center bg-white p-3 shadow">
            <div className="flex items-center">
              <Select 
                options={facilities.map(f => ({ value: f.id, label: facilityDisplayName(f) }))}
                value={{ value: selectedFacility?.id, label: selectedFacilityLabel }}
                onChange={(option) => {
                  const facility = facilities.find(f => f.id === option?.value);
                  if (facility) setSelectedFacility(facility);
                }}
                className="w-48"
                isSearchable
              />
            </div>
          </div>
          
          <div className="flex flex-col justify-center items-center h-64 bg-white p-5 shadow rounded">
            <div className="text-xl font-semibold text-gray-600 mb-2">No Yard Spots Found</div>
            <p className="text-gray-500">There are currently no yard spots available for the selected facility.</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          
          {/* Tabs */}
          {/* <div className="bg-white shadow">
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Current Status" />
              <Tab label="History & Analytics" />
            </Tabs>
          </div>
           */}
          {/* Tab content */}
          {tabValue === 0 && (
            <div>
              {/* Start Screen / Check Controls */}
              {!isYardCheckMode ? (
                 <div className="bg-white p-6 shadow rounded flex flex-col gap-6 max-w-2xl mx-auto mt-8 mb-8">
                  <div className="text-center">
                    <Typography variant="h5" className="font-bold text-gray-800 mb-2">Start Yard Check</Typography>
                    <Typography variant="body1" className="text-gray-600">
                      Select a facility and zone to begin the audit process.
                    </Typography>
                  </div>
                  
                  {/* Warning if yard check was previously active */}
                  {yardCheckId && (
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-amber-700">
                            You have an active yard check in progress for <strong>{zones.find(z => z.id === yardCheckId)?.name || 'a zone'}</strong>. 
                            You must end that check before starting a new one.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Filters Block */}
                  <div className="flex flex-col gap-4">
                    {/* Facility (only show if multiple facilities) */}
                    {facilities.length > 1 && (
                      <div className="w-full">
                         <InputLabel className="mb-1 text-sm font-medium text-gray-700">Facility</InputLabel>
                         <Select 
                            options={facilities.map(f => ({ value: f.id, label: facilityDisplayName(f) }))}
                            value={{ value: selectedFacility?.id, label: selectedFacilityLabel }}
                            onChange={(option) => {
                              const facility = facilities.find(f => f.id === option?.value);
                              if (facility) setSelectedFacility(facility);
                            }}
                            className="w-full"
                            isSearchable
                          />
                      </div>
                    )}

                    {/* Spotter Truck (required at a campus with RTLS) */}
                    {spotterTruckRequired && (
                      <div className="w-full">
                        <InputLabel className="mb-1 text-sm font-medium text-gray-700">Spotter Truck *</InputLabel>
                        <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2">
                          <span className="flex items-center gap-2">
                            <LocalShipping fontSize="small" className="text-gray-500" />
                            {spotterTruck
                              ? <strong>{spotterTruck.name}</strong>
                              : <span className="text-gray-500">No spotter truck set</span>}
                          </span>
                          <Button size="small" startIcon={<Edit />} onClick={() => setSpotterDialogOpen(true)}>
                            {spotterTruck ? 'Edit' : 'Set truck'}
                          </Button>
                        </div>
                      </div>
                    )}
                    {spotterContextError && (
                      <Typography variant="caption" className="text-amber-700">
                        Could not load spotter truck settings. Refresh to try again.
                      </Typography>
                    )}

                    {/* SCAC Filter (Optional) */}
                    <div className="w-full">
                       <InputLabel className="mb-1 text-sm font-medium text-gray-700">
                         Filter by Carrier SCAC <span className="text-gray-500 font-normal">(optional)</span>
                       </InputLabel>
                       <Autocomplete
                         multiple
                         freeSolo
                         options={uniqueSCACs}
                         value={scacFilters}
                         onChange={(event, newValue) => {
                           // Convert all values to uppercase
                           setScacFilters(newValue.map(v => v.toUpperCase()));
                         }}
                         renderInput={(params) => (
                           <TextField
                             {...params}
                             size="small"
                             placeholder={scacFilters.length === 0 ? "Enter or select SCACs..." : ""}
                           />
                         )}
                       />
                       {scacFilters.length > 0 && (
                         <Typography variant="caption" className="text-gray-500 mt-1 block">
                           Showing zones with trailers from: {scacFilters.join(', ')}
                         </Typography>
                       )}
                    </div>
                    
                    {/* Zone */}
                    <div className="w-full">
                       <InputLabel className="mb-1 text-sm font-medium text-gray-700">Zone *</InputLabel>
                       <Select
                          options={
                            orderedCheckZones
                              .map(zone => {
                                const spotCount = allFacilityZones.length > 0
                                  ? (allFacilityZones.find((fz: any) => fz.facility_zone_uuid === zone.id)?.yard_spots?.length || 0)
                                  : yardSpots.filter(s => s.zoneId === zone.id).length;
                                
                                // If SCAC filtering, show breakdown by SCAC
                                const scacMap = filteredZonesBySCAC?.get(zone.id);
                                if (scacMap) {
                                  const totalMatches = Array.from(scacMap.values()).reduce((sum, count) => sum + count, 0);
                                  const scacBreakdown = Array.from(scacMap.entries())
                                    .map(([scac, count]) => `${scac}:${count}`)
                                    .join(', ');
                                  const label = `${zone.name} (${totalMatches} match${totalMatches !== 1 ? 'es' : ''}: ${scacBreakdown} / ${spotCount} spots)`;
                                  return {
                                    value: zone.id,
                                    label
                                  };
                                }
                                
                                return {
                                  value: zone.id,
                                  label: `${zone.name} (${spotCount} spots)`
                                };
                              })
                          }
                          value={
                            filterZone 
                              ? { 
                                  value: filterZone, 
                                  label: (() => {
                                    const zonesForLookup = allZones.length > 0 ? allZones : zones;
                                    const zone = zonesForLookup.find(z => z.id === filterZone);
                                    const spotCount = allFacilityZones.length > 0
                                      ? (allFacilityZones.find((fz: any) => fz.facility_zone_uuid === zone?.id)?.yard_spots?.length || 0)
                                      : yardSpots.filter(s => s.zoneId === filterZone).length;
                                    
                                    // If SCAC filtering, show breakdown by SCAC
                                    const scacMap = filteredZonesBySCAC?.get(filterZone);
                                    if (scacMap) {
                                      const totalMatches = Array.from(scacMap.values()).reduce((sum, count) => sum + count, 0);
                                      const scacBreakdown = Array.from(scacMap.entries())
                                        .map(([scac, count]) => `${scac}:${count}`)
                                        .join(', ');
                                      return zone ? `${zone.name} (${totalMatches} match${totalMatches !== 1 ? 'es' : ''}: ${scacBreakdown} / ${spotCount} spots)` : 'Unknown Zone';
                                    }
                                    
                                    return zone ? `${zone.name} (${spotCount} spots)` : 'Unknown Zone';
                                  })()
                                }
                              : null
                          }
                          onChange={(option) => {
                            // Prevent zone change if yard check is active
                            if (yardCheckId && option?.value !== yardCheckId) {
                              alert('You must end the current yard check before selecting a different zone.');
                              return;
                            }
                            setFilterZone(option?.value || null);
                            setSpotBatchFilter(null);
                            try {
                              if (option?.value) {
                                localStorage.setItem(STORAGE_KEYS.ZONE_FILTER, option.value);
                              } else {
                                localStorage.removeItem(STORAGE_KEYS.ZONE_FILTER);
                              }
                              localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
                            } catch (error) {
                              console.error('Error saving zone filter:', error);
                            }
                          }}
                          className="w-full"
                          placeholder="Select a zone..."
                          isClearable={false}
                        />
                    </div>
                    
                    {/* Spot Range */}
                    {filterZone && (
                      <div className="w-full">
                         <InputLabel className="mb-1 text-sm font-medium text-gray-700">Spot Range *</InputLabel>
                         {generateSpotBatches.length > 0 ? (
                           <Select
                              options={
                                generateSpotBatches
                                  .map(batch => {
                                    // Get all spots in this batch range for the selected zone
                                    const spotsInBatch = yardSpots.filter(spot => {
                                      if (spot.zoneId !== filterZone) return false;
                                      const match = spot.displayNumber.match(/\d+/);
                                      const spotNum = match ? parseInt(match[0], 10) : null;
                                      return spotNum !== null && spotNum >= batch.start && spotNum <= batch.end;
                                    });
                                    
                                    // If SCAC filtering is active, show breakdown
                                    if (scacFilters.length > 0) {
                                      const scacMap = new Map<string, number>();
                                      spotsInBatch.forEach(spot => {
                                        if (spot.carrier_name) {
                                          const carrierUpper = spot.carrier_name.toUpperCase();
                                          scacFilters.forEach(scac => {
                                            if (carrierUpper.includes(scac)) {
                                              scacMap.set(scac, (scacMap.get(scac) || 0) + 1);
                                            }
                                          });
                                        }
                                      });
                                      
                                      const totalMatches = Array.from(scacMap.values()).reduce((sum, count) => sum + count, 0);
                                      
                                      // Return null for ranges with no matches - will filter these out
                                      if (totalMatches === 0) return null;
                                      
                                      const scacBreakdown = Array.from(scacMap.entries())
                                        .map(([scac, count]) => `${scac}:${count}`)
                                        .join(', ');
                                      
                                      return {
                                        value: batch.label,
                                        label: `Spots ${batch.label} (${totalMatches} match${totalMatches !== 1 ? 'es' : ''}: ${scacBreakdown} / ${spotsInBatch.length} spots)`
                                      };
                                    }
                                    
                                    return {
                                      value: batch.label,
                                      label: `Spots ${batch.label} (${spotsInBatch.length} spots)`
                                    };
                                  })
                                  .filter((option): option is { value: string; label: string } => option !== null)
                              }
                              value={
                                spotBatchFilter 
                                  ? { value: spotBatchFilter, label: `Spots ${spotBatchFilter}` }
                                  : null
                              }
                              onChange={(option) => {
                                setSpotBatchFilter(option?.value || null);
                                try {
                                  if (option?.value) {
                                    localStorage.setItem(STORAGE_KEYS.SPOT_BATCH, option.value);
                                  } else {
                                    localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
                                  }
                                } catch (error) {
                                  console.error('Error saving spot batch filter:', error);
                                }
                              }}
                              className="w-full"
                              placeholder="Select a spot range..."
                              isClearable={false}
                            />
                         ) : (
                           <div className="w-full p-3 bg-gray-50 border border-gray-300 rounded text-sm text-gray-700">
                             All {yardSpots.filter(s => {
                               if (s.zoneId !== filterZone) return false;
                               if (scacFilters.length > 0) {
                                 if (!s.carrier_name) return false;
                                 const carrierUpper = s.carrier_name.toUpperCase();
                                 return scacFilters.some(scac => carrierUpper.includes(scac));
                               }
                               return true;
                             }).length} spots in zone{scacFilters.length > 0 ? ` (filtered by ${scacFilters.join(', ')})` : ''}
                           </div>
                         )}
                         <Typography variant="caption" className="block mt-1 text-gray-500">
                           {generateSpotBatches.length > 0 
                             ? `Select a range to check (zone has ${yardSpots.filter(s => {
                               if (s.zoneId !== filterZone) return false;
                               if (scacFilters.length > 0) {
                                 if (!s.carrier_name) return false;
                                 const carrierUpper = s.carrier_name.toUpperCase();
                                 return scacFilters.some(scac => carrierUpper.includes(scac));
                               }
                               return true;
                             }).length} spots${scacFilters.length > 0 ? ' matching filter' : ' total'})`
                             : `This zone has 10 or fewer spots${scacFilters.length > 0 ? ' matching filter' : ''} - all will be checked`}
                         </Typography>
                      </div>
                    )}
                    
                    {/* Options Row */}
                    <div className="flex justify-end items-center mt-2">
                          <Button
                            variant="text"
                            size="small"
                            onClick={clearAllFilters}
                            color="secondary"
                          >
                            Clear Selection
                          </Button>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                     <Button 
                        fullWidth
                        variant="contained" 
                        color="primary" 
                        onClick={handleStartYardCheck}
                        disabled={!filterZone || (generateSpotBatches.length > 0 && !spotBatchFilter) || spotterTruckMissing} // Require zone, spot range and, with RTLS, the truck
                        size="large"
                        className="py-3 text-lg font-bold shadow-md"
                     >
                       Start Yard Check
                     </Button>
                     {(!filterZone || (generateSpotBatches.length > 0 && !spotBatchFilter) || spotterTruckMissing) && (
                       <Typography variant="caption" className="block text-center mt-2 text-gray-500">
                         {spotterTruckMissing
                           ? 'Please enter your spotter truck to start.'
                           : !filterZone
                           ? 'Please select a zone to start.'
                           : 'Please select a spot range to start.'}
                       </Typography>
                     )}
                  </div>
                </div>
              ) : (
                // CHECK MODE CONTROLS
                <div className="mb-4">
                   <Paper className="sticky top-0 z-20 p-4 flex flex-col sm:flex-row justify-between items-center bg-blue-50 border-b border-blue-200 shadow-md gap-4">
                      <div className="flex flex-col">
                         <div className="flex items-center gap-2">
                            <Typography variant="h6" className="text-blue-800 font-bold leading-tight">
                               Yard Check in Progress
                            </Typography>
                            <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs rounded-full font-bold uppercase tracking-wider">Active</span>
                         </div>
                         <Typography variant="body2" className="text-blue-600 mt-1">
                            Zone: <strong>{(() => {
                              const zone = campusCheckZones.find(z => z.id === yardCheckId);
                              return zone ? checkZoneLabel(zone) : zones.find(z => z.id === filterZone)?.name || 'All Zones';
                            })()}</strong>
                            {spotBatchFilter && <span> • Range: <strong>{spotBatchFilter}</strong></span>}
                            <span> • Verified: <strong>{displayedYardSpots.filter(s => verifiedSpots.has(s.id)).length}/{displayedYardSpots.length}</strong></span>
                            {yardCheckRun?.truck && <span> • Truck: <strong>{yardCheckRun.truck.name}</strong></span>}
                         </Typography>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-center sm:justify-end">
                         <div className="flex bg-white rounded shadow-sm overflow-hidden border border-blue-200">
                            <Button 
                               onClick={handleYardCheckPrev} 
                               disabled={
                                 generateSpotBatches.length === 0 || 
                                 !spotBatchFilter ||
                                 generateSpotBatches.findIndex(b => b.label === spotBatchFilter) <= 0
                               }
                               className="px-4"
                               startIcon={<ArrowBack />}
                            >
                               {(() => {
                                 const currentIdx = spotBatchFilter ? generateSpotBatches.findIndex(b => b.label === spotBatchFilter) : -1;
                                 const prevBatch = currentIdx > 0 ? generateSpotBatches[currentIdx - 1] : null;
                                 return prevBatch ? prevBatch.label : 'Prev';
                               })()}
                            </Button>
                            <div className="w-px bg-blue-100"></div>
                            <Button 
                               onClick={handleYardCheckNext} 
                               disabled={
                                 generateSpotBatches.length === 0 ||
                                 !spotBatchFilter ||
                                 generateSpotBatches.findIndex(b => b.label === spotBatchFilter) >= generateSpotBatches.length - 1
                               }
                               className="px-4"
                               endIcon={<ArrowForward />}
                            >
                               {(() => {
                                 const currentIdx = spotBatchFilter ? generateSpotBatches.findIndex(b => b.label === spotBatchFilter) : -1;
                                 const nextBatch = currentIdx >= 0 && currentIdx < generateSpotBatches.length - 1 ? generateSpotBatches[currentIdx + 1] : null;
                                 return nextBatch ? nextBatch.label : 'Next';
                               })()}
                            </Button>
                         </div>
                         
                         <div className="min-w-[220px]">
                            <Select<CheckZoneOption, false, CheckZoneGroup>
                               aria-label="Switch zone"
                               placeholder="Switch zone..."
                               isSearchable
                               isDisabled={advancingZone}
                               value={null}
                               options={checkZoneGroups}
                               isOptionDisabled={option => option.value === yardCheckId}
                               onChange={option => { if (option) handleSwitchZone(option.zone); }}
                               menuPortalTarget={document.body}
                               styles={{ menuPortal: base => ({ ...base, zIndex: 1400 }) }}
                            />
                         </div>

                         <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleSwitchZone(nextCheckZone)}
                            disabled={!nextCheckZone || advancingZone}
                            endIcon={advancingZone ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIos />}
                            className="whitespace-nowrap"
                         >
                            {nextCheckZone ? `Next zone: ${checkZoneLabel(nextCheckZone)}` : 'Last zone'}
                         </Button>

                         <Button 
                            variant="contained" 
                            color="error" 
                            onClick={handleEndYardCheck}
                            disabled={advancingZone}
                            startIcon={<Check />}
                            className="whitespace-nowrap"
                         >
                            End Check
                         </Button>
                      </div>
                   </Paper>
                </div>
              )}
              
              {isYardCheckMode && (
              <>
              {/* Staging Area - only show if staging zone exists in data */}
            
              
              {/* Grouped Yard Spot Display - with fixed height container */}
              <div ref={spotsContainerRef} className="space-y-6" style={{ minHeight: spotsHeight || 'auto' }}>
                {/* Zone-based trailer display (parking lot style) */}
                {zones
                    .sort((a, b) => a.name.localeCompare(b.name)) // Sort zones alphabetically
                    .map(zone => {
                    const zoneColor = zone.color || '#2196F3'; // Default to blue if no color is defined
                    // Get filtered spots for display
                    const zoneSpots = displayedYardSpots.filter(spot => spot.zoneId === zone.id)
                      .sort((a, b) => {
                        const orderDiff = (a.order_number || 0) - (b.order_number || 0);
                        if (orderDiff !== 0) return orderDiff;
                        // Secondary sort by displayNumber when order_number is the same
                        return a.displayNumber.localeCompare(b.displayNumber, undefined, { numeric: true });
                      });
                    
                    // Get ALL zone spots (unfiltered) for adjacency checking - need full list to find true adjacent spots
                    const allZoneSpots = yardSpots.filter(spot => spot.zoneId === zone.id)
                      .sort((a, b) => {
                        const orderDiff = (a.order_number || 0) - (b.order_number || 0);
                        if (orderDiff !== 0) return orderDiff;
                        return a.displayNumber.localeCompare(b.displayNumber, undefined, { numeric: true });
                      });
                      if (filterZone && filterZone !== zone.id) {
                        return null;
                      }
                    const zoneTrailers = trailersByZone.find(group => group.zone.id === zone.id)?.trailers.sort((a, b) => {
                      // Sort by trailer_lineage.created_at descending (most recent first)
                      const aCreatedAt = lineageCheckIn(a.active_lineage);
                      const bCreatedAt = lineageCheckIn(b.active_lineage);
                      
                      if (aCreatedAt && bCreatedAt) {
                        return new Date(bCreatedAt).getTime() - new Date(aCreatedAt).getTime();
                      }
                      
                      // If one has created_at and the other doesn't, prioritize the one with created_at
                      if (aCreatedAt && !bCreatedAt) return -1;
                      if (!aCreatedAt && bCreatedAt) return 1;
                      
                      // If neither has created_at, sort by trailer_number as fallback
                      return (a.trailer_number || '').localeCompare(b.trailer_number || '');
                    }) || [];
                    const hasSpots = zoneSpots.length > 0;
                    const hasTrailers = zoneTrailers.length > 0;
                    const isStaging = zone.zone_type === "staging";
                    
                    return (
                      <div key={zone.id} className={`${isStaging ? 'bg-amber-50 border-2 border-amber-300 border-dashed' : 'bg-white border border-gray-200'} p-4 shadow rounded`}>
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center">
                            {isStaging && <div className="w-4 h-4 bg-amber-400 mr-2 rounded-full animate-pulse"></div>}
                            <h3 className={`font-bold text-lg ${isStaging ? 'text-amber-800' : ''}`} style={{ color: isStaging ? undefined : zoneColor }}>
                              {zone.name}
                            </h3>
                            {isStaging && <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">STAGING</span>}
                          </div>
                          
                        </div>
                        
                        {/* Show yard spots if this zone has them */}

  
                        {/* Show zone trailers if this zone has them */}
                        {hasTrailers && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Unlocated Assets for {zone.name} zone</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                              {
                              zoneTrailers.slice(0, MAX_TILES).map(trailer => (
                                <TileContainer key={`${trailer.puid}-${trailer.trailer_number}`}>
                                  <Tile
                                label={trailer.trailer_number}
                                status={trailer.current_status === 'LOADED' ? 'loaded' : (trailer.current_status === 'EMPTY' || trailer.current_status === 'IN USE') ? 'empty' : 'in-use'}
                                zoneColor={zoneColor}
                                onClick={() => handleShowUnifiedDetails(trailer, 'zone_trailer')}
                                trailerNumber={trailer.trailer_number}
                                carrierName={trailer.carrier_name || undefined}
                                shipmentNumber={trailer.shipment_number || undefined}
                                isZoneTrailer={true}
                                isLocked={mergeLockSets(taskBasedLockedTrailers, lockedZoneTrailers).has(trailer.trailer_uuid || trailer.puid)}
                                onTaskClick={() => {
                                  // Find and show task details for this trailer
                                  const trailerLocks = activeLocks.filter((lock: any) => {
                                    const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                                    return lockUuid === (trailer.trailer_uuid || trailer.puid) && 
                                           (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                                  });
                                  if (trailerLocks.length > 0) {
                                    // Sort locks to prioritize "movement" first
                                    const sortedLocks = [...trailerLocks].sort((a, b) => {
                                      const aNamespace = a.namespace || '';
                                      const bNamespace = b.namespace || '';
                                      if (aNamespace === 'movement' && bNamespace !== 'movement') return -1;
                                      if (bNamespace === 'movement' && aNamespace !== 'movement') return 1;
                                      return aNamespace.localeCompare(bNamespace);
                                    });
                                    handleShowTaskFromLock(sortedLocks[0]);
                                  }
                                }}
                                isTaskBasedLock={taskBasedLockedTrailers.has(trailer.trailer_uuid || trailer.puid)}
                                shipmentDirection={trailer.direction as 'inbound' | 'outbound' | null}
                                locks={activeLocks.filter((lock: any) => {
                                  const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                                  return lockUuid === (trailer.trailer_uuid || trailer.puid) && 
                                         (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                                })}
                                actionButton={!isStaging ? {
                                  label: '×',
                                  onClick: async (e) => {
                                    e.stopPropagation();
                                    if (!trailer.trailer_number) return;
                                    
                                    const confirmed = window.confirm(`Unassign trailer ${trailer.trailer_number}? It will be moved to the staging zone.`);
                                    if (!confirmed) return;
                                    
                                    setIsMovingTrailer(true);
                                    
                                    try {
                                      // Find the staging zone
                                      const stagingZone = allZones.find((z: Zone) => z.zone_type === "staging");
                                      
                                      if (!stagingZone) {
                                        throw new Error('Staging zone not found. Please reload the page.');
                                      }
                                      
                                      // Create movement task to move trailer to staging
                                      await createMovementTask({
                                        trailer_uuid: trailer.trailer_uuid || '',
                                        movement_type: 'facility_zone_to_facility_zone',
                                        facility_id: selectedFacility?.id?.toString() || "1",
                                        source_facility_zone_uuid: trailer.zone_id,
                                        destination_facility_zone_uuid: stagingZone.id,
                                        shipment_id: trailer.shipment_id || undefined,
                                        driver_id: trailer.driver_id || undefined,
                                        auto_complete: true,
                                        force: false,
                                        yard_check: currentYardCheck()
                                      });
                                      
                                      // Refresh data
                                      await refreshData(filterZone || undefined, true);
                                      
                                      setIsMovingTrailer(false);
                                    } catch (error: any) {
                                      console.error('Error unassigning zone trailer:', error);
                                      alert(`Failed to unassign trailer: ${error.message}`);
                                      setIsMovingTrailer(false);
                                    }
                                  },
                                  className: 'bg-red-500 hover:bg-red-600 text-white',
                                  disabled: isMovingTrailer
                                } : undefined}
                                tooltip={
                                  <div className="p-1">
                                    <div className="font-bold">{trailer.trailer_number} - {zone.name}</div>
                                    {trailer.carrier_name && <div className="text-xs">SCAC: {trailer.carrier_name}</div>}
                                    {trailer.driver_name && <div className="text-xs">Driver: {trailer.driver_name}</div>}

                                    <div className="text-xs mt-1">
                                      Direction: {formatDirection(trailer.direction) || 'Unknown'}
                                    </div>
                                    <div className="text-xs">
                                      Status: Zone Trailer
                                    </div>
                                  </div>
                                }
                              />
                                </TileContainer>
                              ))}
                              
                              {zoneTrailers.length > MAX_TILES && (
                                <div 
                                  className="w-32 h-32 rounded shadow flex items-center justify-center bg-gray-100 border-2 border-gray-300 cursor-pointer"
                                  onClick={() => setFilterZone(zone.id)}
                                >
                                  <span className="text-sm font-medium text-gray-600">+{zoneTrailers.length - 24}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {hasSpots && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Yard Spots</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                              {zoneSpots.slice(0, MAX_TILES).map((spot, spotIndex) => {
                                const getSpotStatus = () => {
                                  return spot.status;
                                };

                                // Show move buttons for spots that have trailers (loaded, empty, or in-use)
                                const showMoveButtons = spot.status === 'in-use' || spot.status === 'loaded' || spot.status === 'empty';

                                // Check for adjacent spots in the FULL array (allZoneSpots) for left/right arrows
                                const hasTrailer = showMoveButtons && spot.trailerId;
                                let leftSpotFull: YardSpot | null = null;
                                let rightSpotFull: YardSpot | null = null;
                                
                                // When spot batch filtering is active, find numerically adjacent spots
                                if (spotBatchFilter) {
                                  const currentSpotMatch = spot.displayNumber.match(/\d+/);
                                  const currentSpotNum = currentSpotMatch ? parseInt(currentSpotMatch[0], 10) : null;
                                  
                                  if (currentSpotNum !== null) {
                                    // Find the spot with number currentSpotNum - 1
                                    leftSpotFull = allZoneSpots.find(s => {
                                      const match = s.displayNumber.match(/\d+/);
                                      const num = match ? parseInt(match[0], 10) : null;
                                      return num === currentSpotNum - 1;
                                    }) || null;
                                    
                                    // Find the spot with number currentSpotNum + 1
                                    rightSpotFull = allZoneSpots.find(s => {
                                      const match = s.displayNumber.match(/\d+/);
                                      const num = match ? parseInt(match[0], 10) : null;
                                      return num === currentSpotNum + 1;
                                    }) || null;
                                  }
                                } else {
                                  // When not filtering, use order_number based adjacency
                                  const fullSpotIndex = allZoneSpots.findIndex(s => s.id === spot.id);
                                  leftSpotFull = fullSpotIndex > 0 ? allZoneSpots[fullSpotIndex - 1] : null;
                                  rightSpotFull = fullSpotIndex < allZoneSpots.length - 1 ? allZoneSpots[fullSpotIndex + 1] : null;
                                }
                                
                                // Check if adjacent spot is hidden (not in the displayed slice - either filtered out or cut off by MAX_TILES)
                                // Only show peek for first/last spots in the displayed order
                                const displayedZoneSpots = zoneSpots.slice(0, MAX_TILES);
                                const leftSpotHidden = spotIndex === 0 && leftSpotFull && !displayedZoneSpots.find(s => s.id === leftSpotFull?.id);
                                const rightSpotHidden = spotIndex === displayedZoneSpots.length - 1 && rightSpotFull && !displayedZoneSpots.find(s => s.id === rightSpotFull?.id);
                                
                                
                                // Check if adjacent spots are locked (have active tasks)
                                const leftSpotLocked = leftSpotFull && mergeLockSets(taskBasedLockedSpots, lockedSpots).has(leftSpotFull.id);
                                const rightSpotLocked = rightSpotFull && mergeLockSets(taskBasedLockedSpots, lockedSpots).has(rightSpotFull.id);
                                
                                // Check if adjacent spots have trailers
                                const leftHasTrailer = leftSpotFull && leftSpotFull.trailerId;
                                const rightHasTrailer = rightSpotFull && rightSpotFull.trailerId;
                                
                                // Can move if adjacent spot exists, doesn't have a trailer, and isn't locked
                                const canMoveLeft = hasTrailer && leftSpotFull && !leftSpotFull.trailerId && !leftSpotLocked;
                                const canMoveRight = hasTrailer && rightSpotFull && !rightSpotFull.trailerId && !rightSpotLocked;
                                
                                // Can swap if both spots have trailers and neither is locked
                                const canSwapLeft = hasTrailer && leftHasTrailer && !leftSpotLocked;
                                const canSwapRight = hasTrailer && rightHasTrailer && !rightSpotLocked;

                                return (
                                  <React.Fragment key={spot.id}>
                                    {/* Hidden left spot (if exists) */}
                                    {leftSpotHidden && leftSpotFull && (
                                      <div key={`peek-left-${leftSpotFull.id}`} className="flex items-center justify-center gap-1 opacity-70 pointer-events-none" style={{ filter: 'grayscale(100%)', fontWeight: 'bold' }}>
                                        <div style={{ width: 32 }} />
                                        <Tile
                                          label={leftSpotFull.displayNumber}
                                          status={leftSpotFull.status}
                                          zoneColor={zoneColor}
                                          onClick={() => {}}
                                          spotName={leftSpotFull.displayNumber}
                                          trailerNumber={leftSpotFull.trailerNumber || undefined}
                                          carrierName={leftSpotFull.carrier_name || undefined}
                                          shipmentNumber={leftSpotFull.shipmentNumber || undefined}
                                          isZoneTrailer={false}
                                          isLocked={mergeLockSets(taskBasedLockedSpots, lockedSpots).has(leftSpotFull.id)}
                                          isTaskBasedLock={taskBasedLockedSpots.has(leftSpotFull.id)}
                                          shipmentDirection={(leftSpotFull.direction as 'inbound' | 'outbound' | null) || null}
                                          locks={getLocksForYardSpot(leftSpotFull.id)}
                                          notes={leftSpotFull.notes}
                                        />
                                        <div style={{ width: 32 }} />
                                      </div>
                                    )}
                                    
                                    <TileContainer
                                      showLeftArrow={!!(hasTrailer && leftSpotFull)}
                                      onLeftClick={() => {
                                        if (canSwapLeft) {
                                          handleSwapTrailers(leftSpotFull! as LocalYardSpot, spot);
                                        } else if (canMoveLeft) {
                                          handleMoveToAdjacentSpot(spot, leftSpotFull! as LocalYardSpot);
                                        }
                                      }}
                                      leftArrowDisabled={isMovingTrailer || (!canSwapLeft && !canMoveLeft)}
                                      leftArrowColor={canSwapLeft ? '#10b981' : '#3b82f6'}
                                      leftArrowIcon={canSwapLeft ? <SwapHoriz style={{ fontSize: '20px' }} /> : undefined}
                                      showRightArrow={!!(hasTrailer && rightSpotFull)}
                                      onRightClick={() => {
                                        if (canSwapRight) {
                                          handleSwapTrailers(spot, rightSpotFull! as LocalYardSpot);
                                        } else if (canMoveRight) {
                                          handleMoveToAdjacentSpot(spot, rightSpotFull! as LocalYardSpot);
                                        }
                                      }}
                                      rightArrowDisabled={isMovingTrailer || (!canSwapRight && !canMoveRight)}
                                      rightArrowColor={canSwapRight ? '#10b981' : '#3b82f6'}
                                      rightArrowIcon={canSwapRight ? <SwapHoriz style={{ fontSize: '20px' }} /> : undefined}
                                    >
                                        <Tile
                                    key={spot.id}
                                    label={spot.displayNumber}
                                    status={getSpotStatus()}
                                    zoneColor={zoneColor}
                                    onClick={() => handleShowUnifiedDetails(spot, 'yard_spot')}
                                    spotName={spot.displayNumber}
                                    trailerNumber={spot.trailerNumber || undefined}
                                    carrierName={spot.carrier_name || undefined}
                                    shipmentNumber={spot.shipmentNumber || undefined}
                                    isZoneTrailer={false}
                                    isLocked={mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id)}
                                    isVerified={isYardCheckMode && verifiedSpots.has(spot.id)}
                                    onVerify={isYardCheckMode ? (e) => {
                                      e.stopPropagation();
                                      handleVerifySpot(spot);
                                    } : undefined}
                                    onTaskClick={() => {
                                      const locks = getLocksForYardSpot(spot.id);
                                      if (locks.length > 0) {
                                        // Sort locks to prioritize "movement" first
                                        const sortedLocks = [...locks].sort((a, b) => {
                                          const aNamespace = a.namespace || '';
                                          const bNamespace = b.namespace || '';
                                          if (aNamespace === 'movement' && bNamespace !== 'movement') return -1;
                                          if (bNamespace === 'movement' && aNamespace !== 'movement') return 1;
                                          return aNamespace.localeCompare(bNamespace);
                                        });
                                        handleShowTaskFromLock(sortedLocks[0]);
                                      }
                                    }}
                                    isTaskBasedLock={taskBasedLockedSpots.has(spot.id)}
                                    shipmentDirection={(spot.direction as 'inbound' | 'outbound' | null) || null}
                                    locks={getLocksForYardSpot(spot.id)}
                                    notes={spot.notes}
                                    isHighlighted={false}
                                    actionButton={hasTrailer ? {
                                      label: '×',
                                      onClick: (e) => handleQuickUnassignTrailer(spot, e),
                                      className: 'bg-red-500 hover:bg-red-600 text-white',
                                      disabled: isMovingTrailer || (isYardCheckMode && verifiedSpots.has(spot.id))
                                    } : spot.status === 'available' ? {
                                      label: '+',
                                      onClick: (e) => {
                                        e.stopPropagation();
                                        setQuickAddSpot(spot);
                                        setShowQuickAddModal(true);
                                      },
                                      className: 'bg-blue-500 hover:bg-blue-600 text-white',
                                      disabled: isYardCheckMode && verifiedSpots.has(spot.id)
                                    } : undefined}
                                    tooltip={
                                      <div className="p-1">
                                        <div className="font-bold">{spot.displayNumber} - {zone.name}</div>
                                        {spot.status === 'in-use' && (
                                          <>
                                            {!isEmptyTrailer(spot) && !isLoadedTrailer(spot) ? (
                                              <>
                                                {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                                {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                                {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                                {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                              </>
                                            ) : isEmptyTrailer(spot) ? (
                                              <>
                                                <div className="text-xs">Empty Trailer</div>
                                                {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                                {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                              </>
                                            ) : (
                                              <>
                                                <div className="text-xs">Loaded Trailer</div>
                                                {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                                {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                                {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                                {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                              </>
                                            )}
                                          </>
                                        )}
                                        <div className="text-xs mt-1">
                                          Status: {isEmptyTrailer(spot) && spot.status === 'in-use' ? 'Empty Trailer' : 
                                                  isLoadedTrailer(spot) && spot.status === 'in-use' ? 'Loaded Trailer' : 
                                                  spot.status.charAt(0).toUpperCase() + spot.status.slice(1)}
                                        </div>
                                      </div>
                                    }
                                  />
                                    </TileContainer>
                                
                                {/* Hidden right spot (if exists) */}
                                {rightSpotHidden && rightSpotFull && (
                                  <div key={`peek-right-${rightSpotFull.id}`} className="flex items-center justify-center gap-1 opacity-70 pointer-events-none" style={{ filter: 'grayscale(100%)', fontWeight: 'bold' }}>
                                    <div style={{ width: 32 }} />
                                    <Tile
                                      label={rightSpotFull.displayNumber}
                                      status={rightSpotFull.status}
                                      zoneColor={zoneColor}
                                      onClick={() => {}}
                                      spotName={rightSpotFull.displayNumber}
                                      trailerNumber={rightSpotFull.trailerNumber || undefined}
                                      carrierName={rightSpotFull.carrier_name || undefined}
                                      shipmentNumber={rightSpotFull.shipmentNumber || undefined}
                                      isZoneTrailer={false}
                                      isLocked={mergeLockSets(taskBasedLockedSpots, lockedSpots).has(rightSpotFull.id)}
                                      isTaskBasedLock={taskBasedLockedSpots.has(rightSpotFull.id)}
                                      shipmentDirection={(rightSpotFull.direction as 'inbound' | 'outbound' | null) || null}
                                      locks={getLocksForYardSpot(rightSpotFull.id)}
                                      notes={rightSpotFull.notes}
                                    />
                                    <div style={{ width: 32 }} />
                                  </div>
                                )}
                              </React.Fragment>
                                );
                              })}
                              
                              {zoneSpots.length > MAX_TILES && (
                                <div 
                                  className="w-32 h-32 rounded shadow flex items-center justify-center bg-gray-100 border-2 border-gray-300 cursor-pointer"
                                  onClick={() => setFilterZone(zone.id)}
                                >
                                  <span className="text-sm font-medium text-gray-600">+{zoneSpots.length - 24}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      
                        
                        {/* Show empty zone message if neither spots nor trailers */}
                        {!hasSpots && !hasTrailers && (
                          <div className={`flex items-center justify-center py-8 ${isStaging ? 'text-amber-600 bg-amber-100' : 'text-gray-500 bg-gray-50'} rounded border-2 border-dashed ${isStaging ? 'border-amber-300' : 'border-gray-300'}`}>
                            <LocalParking className="mr-2" />
                            <span>{isStaging ? 'Staging area ready - No trailers currently staged' : 'Empty zone - No spots or trailers assigned'}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
              
              {/* Status Summary - moved to end */}
              {/* Status Summary - moved to end */}
              <div className="flex flex-wrap gap-2 bg-white p-3 shadow mt-6">
                <span className="font-medium mr-2">Status Summary:</span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full mt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-blue-100 border-blue-400 mr-2"></div>
                      <span className="text-sm">Total Trailers</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {zoneTrailers.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-green-100 border-green-400 mr-2"></div>
                      <span className="text-sm">Available</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {yardSpots.filter(spot => spot.status === 'available').length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-blue-100 border-blue-400 mr-2"></div>
                      <span className="text-sm">Loaded Trailer</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {yardSpots.filter(spot => 
                        spot.status === 'loaded').length + 
                       zoneTrailers.filter(trailer => 
                        trailer.current_status === 'LOADED').length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-yellow-200 border-yellow-400 mr-2"></div>
                      <span className="text-sm">Empty Trailer</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {yardSpots.filter(spot => 
                        spot.status === 'empty').length + 
                       zoneTrailers.filter(trailer => 
                        trailer.current_status === 'EMPTY').length}
                    </span>
                  </div>
                </div>
              </div>
              </div>
            </>
            )}
            </div>
          )}
        </div>
      )}



      {/* Zone Management Modal */}
      <ZoneManagementModal
        open={showZoneManageModal}
        zones={zones}
        yardSpots={yardSpots}
        newZoneName={newZoneName}
        newZoneColor={newZoneColor}
        onClose={handleZoneManageClose}
        onShowZoneEdit={handleShowZoneEdit}
        onDeleteZone={handleDeleteZone}
        onAddNewZone={handleAddNewZone}
        onNewZoneNameChange={setNewZoneName}
        onNewZoneColorChange={setNewZoneColor}
      />
      
      {/* Zone Edit Modal */}
      <ZoneEditModal
        open={showZoneEditModal}
        editingZone={editingZone}
        onClose={handleZoneEditClose}
        onSave={handleSaveZone}
        onZoneChange={setEditingZone}
      />



      {/* Add Scheduled Time Modal */}
     
      {/* Move to Dock Modal */}
      <MoveToDockModal
        open={showMoveToDockModal}
        selectedSpot={selectedSpot}
        selectedShipmentPuid={selectedShipmentPuid}
        selectedDockDoor={selectedDockDoor}
        dockFilter={dockFilter}
        autoCompleteMoveToYardSpot={autoCompleteMoveToYardSpot}
        moveToDockError={moveToDockError}
        movingToDock={movingToDock}
        validatingShipment={validatingShipment}
        loadingDockDoors={loadingDockDoors || moveDestinations.loading}
        activeDockDoors={moveDestinations.dockDoors}
        destinationFacilityFilter={campusFilterProps(true)}
        isLoadedTrailer={isLoadedTrailer}
        onClose={handleCloseMoveToDockModal}
        onMove={handleMoveTrailerToDock}
        onShipmentPuidChange={setSelectedShipmentPuid}
        onDockDoorChange={setSelectedDockDoor}
        onDockFilterChange={setDockFilter}
        onAutoCompleteChange={setAutoCompleteMoveToYardSpot}
      />

      {/* Unified Details Modal */}
      <UnifiedDetailsModal
        open={showUnifiedDetailsModal}
        selectedUnifiedItem={selectedUnifiedItem}
        facilityName={selectedFacilityLabel}
        selectedSpot={selectedSpot}
        trailerSearchInput={trailerSearchInput}
        addTrailerScac={addTrailerScac}
        addTrailerNumber={addTrailerNumber}
        addTrailerStatus={addTrailerStatus}
        addTrailerShipmentNumber={addTrailerShipmentNumber}
        addTrailerError={addTrailerError}
        showCreateAnywayButton={showCreateAnywayButton}
        isAddingTrailer={isAddingTrailer}
        isUnassigningTrailer={isUnassigningTrailer}
        isEditingSpotTrailerNotes={isEditingSpotTrailerNotes}
        spotTrailerNotesValue={spotTrailerNotesValue}
        savingSpotTrailerNotes={savingSpotTrailerNotes}
        newShipmentReference={newShipmentReference}
        shipmentReferenceError={shipmentReferenceError}
        isUpdatingShipment={isUpdatingShipment}
        showRemoveShipmentConfirm={showRemoveShipmentConfirm}
        getAllExistingTrailers={() => existingTrailersList}
        isTrailerOOS={isTrailerOOS}
        formatDirection={formatDirection}
        isRemoveShipmentButtonEnabled={isRemoveShipmentButtonEnabled}
        onClose={handleCloseUnifiedModal}
        onMoveSpotToSpot={handleMoveSpotToSpot}
        onMoveZoneTrailerToYardSpot={handleMoveZoneTrailerToYardSpot}
        onMoveToDock={handleMoveToDock}
        onMoveZoneTrailerToDock={handleMoveZoneTrailerToDock}
        onTrailerSearchInputChange={setTrailerSearchInput}
        onAddTrailerScacChange={setAddTrailerScac}
        onAddTrailerNumberChange={setAddTrailerNumber}
        onAddTrailerStatusChange={setAddTrailerStatus}
        onAddTrailerShipmentNumberChange={setAddTrailerShipmentNumber}
        onAddTrailerToSpot={() => handleAddTrailerToSpot()}
        onCreateAnyway={handleCreateAnyway}
        onUnassignTrailer={handleUnassignTrailer}
        onEditSpotTrailerNotes={setIsEditingSpotTrailerNotes}
        onSpotTrailerNotesValueChange={setSpotTrailerNotesValue}
        onSaveSpotTrailerNotes={handleSaveSpotTrailerNotes}
        onNewShipmentReferenceChange={setNewShipmentReference}
        onShipmentReferenceErrorChange={setShipmentReferenceError}
        onUpdateTrailerShipment={handleUpdateTrailerShipment}
        onShowRemoveShipmentConfirm={setShowRemoveShipmentConfirm}
        selectedExistingTrailer={selectedExistingTrailer}
        onAutocompleteExistingTrailerSelect={(newValue) => {
          if (newValue) {
            setSelectedExistingTrailer(newValue);
            setAddTrailerScac(newValue.scac.toUpperCase());
            setAddTrailerNumber(newValue.trailerNumber.toUpperCase());
            setAddTrailerShipmentNumber(newValue.shipmentNumber || '');
            // Set the status from the existing trailer
            if (newValue.trailerStatus) {
              const status = newValue.trailerStatus.toUpperCase();
              if (status === 'EMPTY' || status === 'LOADED') {
                setAddTrailerStatus(status as 'EMPTY' | 'LOADED');
              }
            }
          }
        }}
        onClearExistingTrailer={handleClearExistingTrailer}
      />

      {/* Quick Add Trailer Modal */}
      <Dialog
        open={showQuickAddModal}
        onClose={handleCloseQuickAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Trailer to Spot {quickAddSpot?.displayNumber}
          <IconButton
            onClick={handleCloseQuickAddModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="space-y-6 pt-4">
            {/* Existing Trailer Search */}
            <Autocomplete
              options={existingTrailersList}
              getOptionLabel={(option) => `${option.scac} ${option.trailerNumber}`}
              value={quickAddSelectedExisting}
              onChange={(event, newValue) => {
                if (newValue) {
                  setQuickAddSelectedExisting(newValue);
                  setQuickAddScac(newValue.scac.toUpperCase());
                  setQuickAddNumber(newValue.trailerNumber.toUpperCase());
                  // Set the status from the existing trailer
                  if (newValue.trailerStatus) {
                    const status = newValue.trailerStatus.toUpperCase();
                    if (status === 'EMPTY' || status === 'LOADED') {
                      setQuickAddStatus(status as 'EMPTY' | 'LOADED');
                    }
                  }
                }
              }}
              inputValue={quickAddSearchInput}
              onInputChange={(event, newInputValue) => {
                setQuickAddSearchInput(newInputValue);
              }}
              disabled={isQuickAdding}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Existing Trailers"
                  placeholder="Type SCAC or trailer number..."
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: '1.25rem', padding: '8px' }
                  }}
                  InputLabelProps={{
                    style: { fontSize: '1.125rem' }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} style={{ fontSize: '1.125rem', padding: '12px' }}>
                  <div>
                    <div className="font-bold">{option.scac} {option.trailerNumber}</div>
                    {option.location && (
                      <div className="text-sm text-gray-600">{option.location}</div>
                    )}
                  </div>
                </li>
              )}
            />
            
            {quickAddSelectedExisting && (
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setQuickAddSelectedExisting(null);
                    setQuickAddScac('');
                    setQuickAddNumber('');
                    setQuickAddStatus('EMPTY');
                    setQuickAddSearchInput('');
                  }}
                  size="small"
                  sx={{ fontSize: '1rem' }}
                >
                  Clear Selection
                </Button>
              </div>
            )}
            
            <TextField
              label="SCAC"
              value={quickAddScac}
              onChange={(e) => setQuickAddScac(e.target.value.toUpperCase())}
              fullWidth
              disabled={isQuickAdding || !!quickAddSelectedExisting}
              InputProps={{
                style: { fontSize: '1.25rem', padding: '16px' },
                readOnly: !!quickAddSelectedExisting
              }}
              InputLabelProps={{
                style: { fontSize: '1.125rem' }
              }}
            />
            
            <TextField
              label="Trailer Number"
              value={quickAddNumber}
              onChange={(e) => setQuickAddNumber(e.target.value.toUpperCase())}
              fullWidth
              disabled={isQuickAdding || !!quickAddSelectedExisting}
              InputProps={{
                style: { fontSize: '1.25rem', padding: '16px' },
                readOnly: !!quickAddSelectedExisting
              }}
              InputLabelProps={{
                style: { fontSize: '1.125rem' }
              }}
            />
            
            <FormControl fullWidth disabled={isQuickAdding || !!quickAddSelectedExisting}>
              <InputLabel style={{ fontSize: '1.125rem' }}>Status</InputLabel>
              <MuiSelect
                value={quickAddStatus}
                onChange={(e) => setQuickAddStatus(e.target.value as 'EMPTY' | 'LOADED')}
                label="Status"
                style={{ fontSize: '1.25rem', padding: '8px' }}
              >
                <MenuItem value="EMPTY" style={{ fontSize: '1.125rem' }}>Empty</MenuItem>
                <MenuItem value="LOADED" style={{ fontSize: '1.125rem' }}>Loaded</MenuItem>
              </MuiSelect>
            </FormControl>
            
            {quickAddError && (
              <div className="space-y-2">
                <div className="text-red-600 text-sm">
                  {quickAddError}
                </div>
                {showQuickAddCreateAnywayButton && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleQuickAddCreateAnyway}
                    disabled={isQuickAdding}
                    fullWidth
                    sx={{ fontSize: '1.125rem', padding: '12px 24px' }}
                  >
                    Create Anyway
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseQuickAddModal}
            disabled={isQuickAdding}
            sx={{ fontSize: '1.125rem', padding: '12px 24px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleQuickAddTrailer()}
            variant="contained"
            disabled={isQuickAdding}
            sx={{ fontSize: '1.125rem', padding: '12px 24px' }}
          >
            {isQuickAdding ? 'Adding...' : 'Add Trailer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <TaskSuccessModal
        open={showSuccessModal}
        createdTask={createdTask}
        onClose={() => {
          setShowSuccessModal(false);
          setCreatedTask(null);
        }}
      />
      
      {/* Move to Yard Spot Modal */}
      <SpotterTruckDialog
        open={spotterDialogOpen}
        facilityId={selectedFacilityId}
        trucks={spotterContext?.spotter_trucks || []}
        currentName={spotterTruck?.name}
        onClose={() => setSpotterDialogOpen(false)}
        onSaved={handleSpotterTruckSaved}
      />

      <MoveToYardSpotModal
        key={`move-to-yard-modal-${allZones.length}-${allFacilityZones.length}`}
        open={showMoveToYardSpotModal}
        selectedStagingTrailer={selectedStagingTrailer}
        selectedShipmentPuid={selectedShipmentPuid}
        selectedYardSpotForMove={selectedYardSpotForMove}
        selectedYardSpotUuid={selectedYardSpotUuid}
        selectedZoneFilter={selectedZoneFilter}
        spotFilter={spotFilter}
        autoCompleteMoveToYardSpot={autoCompleteMoveToYardSpot}
        assigningYardSpot={assigningYardSpot}
        assignYardSpotError={assignYardSpotError}
        validatingShipment={validatingShipment}
        selectedDestinationType={selectedDestinationType}
        selectedFacilityZone={selectedFacilityZone}
        zones={moveDestinations.zones}
        yardSpots={moveDestinations.yardSpots}
        facilityZones={moveDestinations.facilityZones}
        destinationFacilityFilter={campusFilterProps(false)}
        taskBasedLockedSpots={moveDestinations.lockedSpots}
        lockedSpots={lockedSpots}
        mergeLockSets={mergeLockSets}
        onClose={handleCloseMoveToYardSpotModal}
        onAssign={handleAssignYardSpot}
        onShipmentPuidChange={setSelectedShipmentPuid}
        onYardSpotForMoveChange={setSelectedYardSpotForMove}
        onYardSpotUuidChange={setSelectedYardSpotUuid}
        onZoneFilterChange={setSelectedZoneFilter}
        onSpotFilterChange={setSpotFilter}
        onAutoCompleteChange={setAutoCompleteMoveToYardSpot}
        onDestinationTypeChange={setSelectedDestinationType}
        onFacilityZoneChange={setSelectedFacilityZone}
        onAssignYardSpotErrorChange={setAssignYardSpotError}
      />

      {/* Zone Trailer Details Modal */}
      <ZoneTrailerDetailsModal
        open={showZoneTrailerDetailsModal}
        selectedZoneTrailer={selectedZoneTrailer}
        trailerNotesValue={trailerNotesValue}
        isEditingTrailerNotes={isEditingTrailerNotes}
        savingTrailerNotes={savingTrailerNotes}
        isTrailerOOS={isTrailerOOS}
        formatDirection={formatDirection}
        onClose={handleCloseZoneTrailerDetailsModal}
        onMoveToYardSpot={handleMoveZoneTrailerToYardSpot}
        onMoveToDock={handleMoveZoneTrailerToDock}
        onTrailerNotesChange={setTrailerNotesValue}
        onEditTrailerNotes={setIsEditingTrailerNotes}
        onSaveTrailerNotes={handleSaveTrailerNotes}
      />

      {/* Task Details Modal */}
      <TaskDetailsModal
        open={showTaskDetailsModal}
        onClose={() => {
          setShowTaskDetailsModal(false);
          setSelectedTaskForDetails(null);
        }}
        task={selectedTaskForDetails}
      />

      <RemoveShipmentConfirmDialog
        open={showRemoveShipmentConfirm}
        trailerNumber={selectedSpot?.trailerNumber || selectedUnifiedItem?.trailerNumber}
        shipmentNumber={selectedSpot?.shipmentNumber || selectedUnifiedItem?.shipmentNumber}
        isRemoving={isRemovingShipment}
        onClose={() => setShowRemoveShipmentConfirm(false)}
        onConfirm={handleRemoveShipmentRelationship}
      />
    </div>
  );
});

export default YardSpotterManagement;
